import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut, getAuth, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, updateDoc, getDoc, getDocs, getCountFromServer, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, firebaseConfig } from "../firebase";
import "./styles/admin.css";
import InsightsDashboard from "../pages/InsightsDashboard";
import TicketScanner from "./TicketScanner";
import ServiceManagementPanel from "../components/booking/ServiceManagementPanel";
import { usePushNotifications } from "../hooks/usePushNotifications";

import {
	LayoutDashboard, CalendarDays, TrendingUp, BookOpen, Ticket, Newspaper,
	ScanLine, LogOut, CalendarClock, Users, Award, ImagePlus, FileText,
	Pencil, Trash2, Plus, Inbox, Clock, Smartphone, ChevronLeft, ChevronRight,
	ArrowUp, ArrowDown, Video, Radio, FileEdit, ChevronDown, Mic2, ShoppingBag,
	Settings2, X, Search, Copy, Mail, Phone, MapPin, Check, Package, Truck, Database,
	UserPlus, Eye, EyeOff, ShieldCheck, KeyRound, Sun, Moon, AlertCircle, Sparkles, Target,
} from "lucide-react";

import { supabase } from "../supabaseClient";

import { useBookingWatcher } from "../hooks/useBookingWatcher";
import BookingToast from "../components/BookingToast";
import BlogsTab from "./BlogsTab";
import UnpaidCustomersTab from "./UnpaidCustomersTab";
import { sendBookDeliveryUpdate } from "../utils/bookNotifications";

const STORAGE_BUCKETS = ["book-pdfs", "book-covers", "keynote-photos", "keynote-docs", "blog-covers"];
const TRACKED_STORAGE_LIMIT = 1024 * 1024 * 1024;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024;
const TEAM_RESTRICTED_TAB_IDS = new Set(["commercial", "unpaid"]);
const BOOKING_TEAM_RESTRICTED_FIELDS = new Set([
	"price", "paymentStatus", "paymentMethod", "amount", "amountCents",
	"paidAt", "paidMarkedManually", "paidMarkedBy", "paymentChoice", "quoteOnly",
	"checkoutId", "paymentReference", "zoomMeeting", "rescheduleTokenHash",
]);

function formatBytes(bytes) {
	if (!bytes) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

function validateUpload(file, { allowedTypes, maxBytes, label }) {
	if (!file) throw new Error(`Please choose a ${label} file.`);
	if (file.size > maxBytes) {
		throw new Error(`${label} is too large. The maximum size is ${formatBytes(maxBytes)}.`);
	}
	if (allowedTypes.length && !allowedTypes.includes(file.type)) {
		throw new Error(`Unsupported ${label} type. Please choose a supported file format.`);
	}
}

const AVATAR_COLORS = ["teal", "amber", "purple", "rose", "blue", "green"];

function avatarColorFor(name = "") {
	const sum = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
	return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function initialsFor(name = "") {
	return name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((w) => w[0])
		.join("")
		.toUpperCase();
}

// Maps Firebase Auth error codes to plain-language messages for the
// "add team member" form.
function friendlyAuthError(err) {
	const code = err?.code || "";
	if (code === "auth/email-already-in-use") return "That email already has an account.";
	if (code === "auth/invalid-email") return "That doesn't look like a valid email address.";
	if (code === "auth/weak-password") return "Password is too weak — use at least 6 characters.";
	if (code === "auth/network-request-failed") return "Network error — check your connection and try again.";
	return err?.message || "Couldn't create that account. Please try again.";
}

// A readable, hard-to-guess password: no ambiguous characters (0/O, 1/l/I).
function generateSecurePassword(length = 12) {
	const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*";
	const randomValues = new Uint32Array(length);
	window.crypto.getRandomValues(randomValues);
	let out = "";
	for (let i = 0; i < length; i++) out += chars[randomValues[i] % chars.length];
	return out;
}

function omitTeamRestrictedBookingFields(booking) {
	return Object.fromEntries(
		Object.entries(booking).filter(([field]) => !BOOKING_TEAM_RESTRICTED_FIELDS.has(field))
	);
}

function zoomJoinUrl(booking) {
	const value = booking?.zoomMeeting?.joinUrl;
	if (typeof value !== "string") return "";
	try {
		const url = new URL(value);
		return url.protocol === "https:" && /(^|\.)zoom\.us$/i.test(url.hostname) ? url.toString() : "";
	} catch {
		return "";
	}
}


async function supabaseUploadPdf(file, onProgress) {
	validateUpload(file, {
		allowedTypes: ["application/pdf"],
		maxBytes: MAX_DOCUMENT_SIZE,
		label: "PDF",
	});
	onProgress(10);
	const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
	const path = `${Date.now()}-${cleanName}`;

	const { data, error } = await supabase.storage
		.from("book-pdfs")
		.upload(path, file, {
			cacheControl: "3600",
			upsert: false,
			contentType: "application/pdf",
		});

	if (error) throw new Error(error.message || "PDF upload failed");
	onProgress(90);

	const { data: publicUrlData } = supabase.storage
		.from("book-pdfs")
		.getPublicUrl(data.path);

	onProgress(100);
	return { url: publicUrlData.publicUrl, path: data.path };
}

// Deletes a PDF from Supabase Storage by its stored path.
async function supabaseDeletePdf(path) {
	if (!path) return;
	const { error } = await supabase.storage.from("book-pdfs").remove([path]);
	if (error) console.error("[Supabase] delete failed:", error.message);
}

// Unsigned-equivalent image upload for book covers, stored in Supabase
// Storage instead of Cloudinary. Requires a public "book-covers" bucket
// in the Supabase dashboard (Storage → New bucket → Public bucket: ON).
async function supabaseUploadImage(file, onProgress) {
	validateUpload(file, {
		allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
		maxBytes: MAX_IMAGE_SIZE,
		label: "cover image",
	});
	onProgress(10);
	const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
	const path = `${Date.now()}-${cleanName}`;

	const { data, error } = await supabase.storage
		.from("book-covers")
		.upload(path, file, {
			cacheControl: "3600",
			upsert: false,
			contentType: file.type || "image/jpeg",
		});

	if (error) throw new Error(error.message || "Cover upload failed");
	onProgress(90);

	const { data: publicUrlData } = supabase.storage
		.from("book-covers")
		.getPublicUrl(data.path);

	onProgress(100);
	return { url: publicUrlData.publicUrl, path: data.path };
}

// Deletes a cover image from Supabase Storage by its stored path.
async function supabaseDeleteImage(path) {
	if (!path) return;
	const { error } = await supabase.storage.from("book-covers").remove([path]);
	if (error) console.error("[Supabase] cover delete failed:", error.message);
}

// Keynote photo upload/delete — same pattern as book covers, separate bucket
// so keynote media doesn't mix with book assets. Requires a public
// "keynote-photos" bucket in the Supabase dashboard.
async function supabaseUploadKeynotePhoto(file, onProgress) {
	validateUpload(file, {
		allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
		maxBytes: MAX_IMAGE_SIZE,
		label: "photo",
	});
	onProgress(10);
	const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
	const path = `${Date.now()}-${cleanName}`;

	const { data, error } = await supabase.storage
		.from("keynote-photos")
		.upload(path, file, {
			cacheControl: "3600",
			upsert: false,
			contentType: file.type || "image/jpeg",
		});

	if (error) throw new Error(error.message || "Photo upload failed");
	onProgress(90);

	const { data: publicUrlData } = supabase.storage
		.from("keynote-photos")
		.getPublicUrl(data.path);

	onProgress(100);
	return { url: publicUrlData.publicUrl, path: data.path };
}

async function supabaseDeleteKeynotePhoto(path) {
	if (!path) return;
	const { error } = await supabase.storage.from("keynote-photos").remove([path]);
	if (error) console.error("[Supabase] keynote photo delete failed:", error.message);
}

// Keynote synopsis (PDF) upload/delete. Requires a public "keynote-docs"
// bucket in the Supabase dashboard.
async function supabaseUploadKeynoteSynopsis(file, onProgress) {
	validateUpload(file, {
		allowedTypes: ["application/pdf"],
		maxBytes: MAX_DOCUMENT_SIZE,
		label: "synopsis PDF",
	});
	onProgress(10);
	const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
	const path = `${Date.now()}-${cleanName}`;

	const { data, error } = await supabase.storage
		.from("keynote-docs")
		.upload(path, file, {
			cacheControl: "3600",
			upsert: false,
			contentType: "application/pdf",
		});

	if (error) throw new Error(error.message || "Synopsis upload failed");
	onProgress(90);

	const { data: publicUrlData } = supabase.storage
		.from("keynote-docs")
		.getPublicUrl(data.path);

	onProgress(100);
	return { url: publicUrlData.publicUrl, path: data.path };
}

async function supabaseDeleteKeynoteSynopsis(path) {
	if (!path) return;
	const { error } = await supabase.storage.from("keynote-docs").remove([path]);
	if (error) console.error("[Supabase] keynote synopsis delete failed:", error.message);
}


const CLOUDINARY_CLOUD = "domll86rn";
const CLOUDINARY_PRESET = "uploadbooks";
const CLOUDINARY_UPLOAD = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`;
const CLOUDINARY_DELETE = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/destroy`;

const CLOUDINARY_RAW_UPLOAD = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/raw/upload`;

const API_BASE = "https://jabuzwane.vercel.app/api";

// Calendar endpoints only accept a current Firebase ID token, never a Gmail
// address or Google credential from the browser. The backend stores the Google
// refresh token encrypted and performs each calendar write server-side.
async function calendarApiRequest(calendarAction, payload = {}) {
	const user = auth.currentUser;
	if (!user) throw new Error("Sign in to the dashboard before managing Google Calendar.");
	const idToken = await user.getIdToken();
	const res = await fetch(`${API_BASE}/notify-booking`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${idToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ calendarAction, ...payload }),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(body.message || "Google Calendar request failed.");
	return body;
}

async function syncBookingWithGoogleCalendar(bookingId, action = "sync") {
	return calendarApiRequest("sync", { bookingId, bookingAction: action });
}

// Unsigned image upload — returns { url, publicId }
async function cloudinaryUpload(file, onProgress) {
	return new Promise((resolve, reject) => {
		const fd = new FormData();
		fd.append("file", file);
		fd.append("upload_preset", CLOUDINARY_PRESET);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", CLOUDINARY_UPLOAD);

		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
		};
		xhr.onload = () => {
			const res = JSON.parse(xhr.responseText);
			if (xhr.status === 200) {
				resolve({ url: res.secure_url, publicId: res.public_id });
			} else {
				reject(new Error(res.error?.message || "Image upload failed"));
			}
		};
		xhr.onerror = () => reject(new Error("Network error during image upload"));
		xhr.send(fd);
	});
}

// Unsigned raw file upload (PDF) — returns { url, publicId }
async function cloudinaryUploadRaw(file, onProgress) {
	return new Promise((resolve, reject) => {
		const fd = new FormData();
		fd.append("file", file);
		fd.append("upload_preset", CLOUDINARY_PRESET);
		// Marking the delivery type "authenticated" would be ideal for paid
		// content, but that requires a SIGNED upload (server-side), which
		// unsigned presets can't do. For now this PDF is uploaded as a normal
		// raw asset — meaning the Cloudinary URL itself is guessable/public if
		// leaked. Real access-gating happens later: the *download link shown
		// to the buyer* is a short-lived signed URL generated by a Cloud
		// Function only after payment succeeds (see yocoWebhook.js). The raw
		// Cloudinary URL stored in Firestore is never exposed to the public
		// book listing — only to the admin dashboard and the signing function.

		const xhr = new XMLHttpRequest();
		xhr.open("POST", CLOUDINARY_RAW_UPLOAD);

		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
		};
		xhr.onload = () => {
			const res = JSON.parse(xhr.responseText);
			if (xhr.status === 200) {
				resolve({ url: res.secure_url, publicId: res.public_id });
			} else {
				reject(new Error(res.error?.message || "PDF upload failed"));
			}
		};
		xhr.onerror = () => reject(new Error("Network error during PDF upload"));
		xhr.send(fd);
	});
}

// Note: unsigned presets cannot delete — you'd need a signed request via a
// backend function. For now we just remove the Firestore record and leave the
// asset on Cloudinary (free tier has plenty of space). To enable deletion,
// add a small Firebase Cloud Function or a Netlify/Vercel function that signs
// the delete request with your API secret.
async function cloudinaryDelete(publicId) {
	// No-op on the free client-side path — see note above.
	console.info("[Cloudinary] asset left on CDN:", publicId);
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
	"January", "February", "March", "April", "May", "June",
	"July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const SERVICE_COLORS = {
	"relationship-counselling": "#0d9488",
	"individual-counselling": "#14b8a6",
	"coaching": "#0f766e",
	"keynote-speaker": "#2dd4bf",
};

const SERVICE_CHART_COLORS = ["#0d9488", "#14b8a6", "#0f766e", "#2dd4bf", "#5eead4", "#99f6e4"];

// ── Media tab constants ───────────────────────────────────────────────────────

const MEDIA_TYPE_OPTIONS = [
	{ id: "video", label: "Video" },
	{ id: "radio", label: "Audio" },
	{ id: "press", label: "Press" },
];

const BOOKING_STATUS_OPTIONS = [
	{ id: "confirmed", label: "Confirmed" },
	{ id: "pending", label: "Pending" },
	{ id: "cancelled", label: "Cancelled" },
];

const defaultCta = (type) =>
	type === "video" ? "Watch on YouTube" : type === "radio" ? "Listen now" : "Read article";

// Extracts a YouTube video ID from common URL shapes, used to preview the
// thumbnail in the admin form before saving.
function getYouTubeId(url) {
	try {
		const u = new URL(url);
		if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
		if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
	} catch {
		// not a valid URL yet — fine while the admin is still typing
	}
	return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toISODate(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function toYearMonth(year, month) {
	return `${year}-${String(month + 1).padStart(2, "0")}`;
}

const REFERRAL_SOURCE_LABELS = {
	linkedin: "LinkedIn",
	facebook: "Facebook",
	tiktok: "TikTok",
	youtube: "YouTube",
	google: "Google",
	instagram: "Instagram",
};

function getMarketingSnapshot(bookings, monthlyTrend, periodLabel) {
	const statusCounts = { confirmed: 0, pending: 0, cancelled: 0, other: 0 };
	const services = new Map();
	const referralSources = new Map();
	const modes = new Map();
	let referralCount = 0;
	const weekdays = new Map();

	bookings.forEach((booking) => {
		const status = String(booking.status || "pending").toLowerCase();
		statusCounts[status] = (statusCounts[status] || 0) + 1;

		const service = String(booking.serviceTitle || booking.service || "Other")
			.trim()
			.split(/\s+[—–-]\s+/)[0] || "Other";
		services.set(service, (services.get(service) || 0) + 1);

		if (booking.referralSource) {
			referralCount += 1;
			const referral = REFERRAL_SOURCE_LABELS[booking.referralSource] || String(booking.referralSource);
			referralSources.set(referral, (referralSources.get(referral) || 0) + 1);
		}

		const mode = String(booking.mode || "").trim();
		if (mode) modes.set(mode, (modes.get(mode) || 0) + 1);

		if (booking.date) {
			const date = new Date(`${booking.date}T12:00:00`);
			if (!Number.isNaN(date.getTime())) {
				const weekday = DAY_LABELS[date.getDay()];
				weekdays.set(weekday, (weekdays.get(weekday) || 0) + 1);
			}
		}
	});

	const sortCounts = (counts) => [...counts.entries()]
		.map(([label, count]) => ({ label: label.slice(0, 60), count }))
		.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
		.slice(0, 5);

	return {
		period: periodLabel,
		currentPeriod: {
			totalBookings: bookings.length,
			statusCounts,
			referralResponses: referralCount,
			referralResponseRate: bookings.length ? Math.round((referralCount / bookings.length) * 100) : 0,
			topServices: sortCounts(services),
			referralSources: sortCounts(referralSources),
			sessionModes: sortCounts(modes),
			busiestWeekdays: sortCounts(weekdays),
		},
		monthlyTrend: monthlyTrend.map(({ label, count }) => ({ label, count })),
	};
}

function formatDisplayDate(isoDate) {
	return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-ZA", {
		weekday: "long", day: "numeric", month: "long", year: "numeric",
	});
}
function formatCurrency(val) {
	return new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(val);
}

// Returns a friendly display name from an email, e.g. "jabu.zwane@x.com" -> "Jabu Zwane"
function nameFromEmail(email) {
	if (!email) return "there";
	const local = email.split("@")[0];
	return local
		.split(/[._-]+/)
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

// Initials for the avatar, e.g. "jabu.zwane@x.com" -> "JZ"
function initialsFromEmail(email) {
	if (!email) return "?";
	const local = email.split("@")[0];
	const parts = local.split(/[._-]+/).filter(Boolean);
	if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
	return local.slice(0, 2).toUpperCase();
}

function timeGreeting() {
	const h = new Date().getHours();
	if (h < 5) return "Working late";
	if (h < 12) return "Good morning";
	if (h < 17) return "Good afternoon";
	if (h < 21) return "Good evening";
	return "Working late";
}

// ── Trend chart: bar or line, switchable ──────────────────────────────────────

const TrendChart = ({ data, type }) => {
	if (!data.length) return <div className="adm-chart-empty">No data yet</div>;
	const max = Math.max(...data.map(d => d.count), 1);
	const H = 144;
	const LABEL_H = 26;
	const PAD_L = 8;
	const PAD_R = 8;

	if (type === "bar") {
		const BAR_W = 28, GAP = 14;
		const totalW = data.length * (BAR_W + GAP) - GAP;
		const svgW = Math.max(totalW + PAD_L + PAD_R, 300);
		return (
			<svg viewBox={`0 0 ${svgW} ${H + LABEL_H}`} width="100%" aria-label="Monthly bookings bar chart">
				<defs>
					<linearGradient id="admBarGrad" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#14b8a6" stopOpacity="0.95" />
						<stop offset="100%" stopColor="#0d9488" stopOpacity="0.55" />
					</linearGradient>
				</defs>
				{data.map((d, i) => {
					const barH = Math.max(4, Math.round((d.count / max) * (H - 18)));
					const x = PAD_L + i * (BAR_W + GAP);
					const y = H - barH;
					return (
						<g key={d.label}>
							<rect x={x} y={6} width={BAR_W} height={H - 6} rx="6" fill="var(--adm-surface-raised)" />
							<rect x={x} y={y} width={BAR_W} height={barH} rx="6" fill="url(#admBarGrad)" />
							{d.count > 0 && (
								<text x={x + BAR_W / 2} y={y - 7} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--adm-ink-soft)">
									{d.count}
								</text>
							)}
							<text x={x + BAR_W / 2} y={H + LABEL_H - 8} textAnchor="middle" fontSize="10.5" fill="var(--adm-ink-faint)">
								{d.label}
							</text>
						</g>
					);
				})}
			</svg>
		);
	}

	// line chart
	const svgW = 320;
	const stepX = (svgW - PAD_L - PAD_R) / (data.length - 1 || 1);
	const points = data.map((d, i) => {
		const x = PAD_L + i * stepX;
		const y = 8 + (H - 16) - (d.count / max) * (H - 16);
		return { x, y, ...d };
	});
	const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
	const areaPath = `${linePath} L${points[points.length - 1].x},${H} L${points[0].x},${H} Z`;

	return (
		<svg viewBox={`0 0 ${svgW} ${H + LABEL_H}`} width="100%" aria-label="Monthly bookings line chart">
			<defs>
				<linearGradient id="admLineAreaGrad" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="#14b8a6" stopOpacity="0.18" />
					<stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
				</linearGradient>
			</defs>
			{/* baseline grid */}
			<line x1={PAD_L} y1={H} x2={svgW - PAD_R} y2={H} stroke="var(--adm-line)" strokeWidth="1" />
			<path d={areaPath} fill="url(#admLineAreaGrad)" />
			<path d={linePath} fill="none" stroke="#14b8a6" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
			{points.map((p, i) => (
				<g key={i}>
					<circle cx={p.x} cy={p.y} r="3.5" fill="var(--adm-surface)" stroke="#14b8a6" strokeWidth="2" />
					{p.count > 0 && (
						<text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--adm-ink-soft)">
							{p.count}
						</text>
					)}
					<text x={p.x} y={H + LABEL_H - 8} textAnchor="middle" fontSize="10.5" fill="var(--adm-ink-faint)">
						{p.label}
					</text>
				</g>
			))}
		</svg>
	);
};

// ── Smooth area chart (modern dashboard style) with hover tooltip ─────────────

const smoothPath = (pts) => {
	if (pts.length < 2) return `M${pts[0].x},${pts[0].y}`;
	let d = `M${pts[0].x},${pts[0].y}`;
	for (let i = 0; i < pts.length - 1; i++) {
		const p0 = pts[i], p1 = pts[i + 1];
		const midX = (p0.x + p1.x) / 2;
		d += ` C${midX},${p0.y} ${midX},${p1.y} ${p1.x},${p1.y}`;
	}
	return d;
};

const AreaTrendChart = ({ data }) => {
	const [hoverIndex, setHoverIndex] = useState(null);
	const svgRef = useRef(null);
	if (!data.length) return <div className="adm-chart-empty">No data yet</div>;

	const W = 640, H = 200, PAD_L = 12, PAD_R = 12, PAD_T = 20, PAD_B = 30;
	const max = Math.max(...data.map(d => d.count), 1);
	const stepX = (W - PAD_L - PAD_R) / (data.length - 1 || 1);
	const innerH = H - PAD_T - PAD_B;

	const points = data.map((d, i) => ({
		x: PAD_L + i * stepX,
		y: PAD_T + innerH - (d.count / max) * innerH,
		...d,
	}));

	const linePath = smoothPath(points);
	const areaPath = `${linePath} L${points[points.length - 1].x},${H - PAD_B} L${points[0].x},${H - PAD_B} Z`;

	const handleMove = (e) => {
		if (!svgRef.current) return;
		const rect = svgRef.current.getBoundingClientRect();
		const px = ((e.clientX - rect.left) / rect.width) * W;
		let closest = 0, dist = Infinity;
		points.forEach((p, i) => {
			const dd = Math.abs(p.x - px);
			if (dd < dist) { dist = dd; closest = i; }
		});
		setHoverIndex(closest);
	};

	const hovered = hoverIndex != null ? points[hoverIndex] : null;

	return (
		<div className="adm-area-chart-wrap">
			<svg
				ref={svgRef}
				viewBox={`0 0 ${W} ${H}`}
				width="100%"
				onMouseMove={handleMove}
				onMouseLeave={() => setHoverIndex(null)}
				aria-label="Monthly bookings trend"
			>
				<defs>
					<linearGradient id="admAreaGrad" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#14b8a6" stopOpacity="0.28" />
						<stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
					</linearGradient>
				</defs>

				{[0.25, 0.5, 0.75, 1].map(f => (
					<line
						key={f}
						x1={PAD_L} x2={W - PAD_R}
						y1={PAD_T + innerH * (1 - f)} y2={PAD_T + innerH * (1 - f)}
						stroke="var(--adm-line)" strokeDasharray="3 4" strokeWidth="1"
					/>
				))}

				<path d={areaPath} fill="url(#admAreaGrad)" />
				<path d={linePath} fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

				{hovered && (
					<line x1={hovered.x} x2={hovered.x} y1={PAD_T} y2={H - PAD_B} stroke="var(--adm-line)" strokeDasharray="3 3" />
				)}

				{points.map((p, i) => (
					<circle
						key={i} cx={p.x} cy={p.y}
						r={hoverIndex === i ? 5 : 3}
						fill="#fff" stroke="#14b8a6" strokeWidth="2"
						style={{ transition: "r 0.15s ease" }}
					/>
				))}

				{points.map((p, i) => (
					<text key={`l-${i}`} x={p.x} y={H - 10} textAnchor="middle" fontSize="11" fill="var(--adm-ink-faint)">
						{p.label}
					</text>
				))}
			</svg>

			{hovered && (
				<div className="adm-chart-tooltip" style={{ left: `${(hovered.x / W) * 100}%`, top: `${(hovered.y / H) * 100}%` }}>
					<div className="adm-chart-tooltip__date">{hovered.label}</div>
					<div className="adm-chart-tooltip__row">
						<span className="adm-chart-tooltip__dot" />
						Bookings&nbsp;<strong>{hovered.count}</strong>
					</div>
				</div>
			)}
		</div>
	);
};

// ── Service breakdown: monthly volume and share at a glance ────────────────────

const polarToXY = (cx, cy, r, angleDeg) => {
	const rad = (angleDeg - 90) * (Math.PI / 180);
	return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const PieSlices = ({ breakdown, total, cx, cy, r }) => {
	let startAngle = 0;
	const innerR = r * 0.58;
	return breakdown.map(([title, count], i) => {
		const endAngle = startAngle + (count / total) * 360;
		const [x1, y1] = polarToXY(cx, cy, r, startAngle);
		const [x2, y2] = polarToXY(cx, cy, r, endAngle);
		const [ix1, iy1] = polarToXY(cx, cy, innerR, endAngle);
		const [ix2, iy2] = polarToXY(cx, cy, innerR, startAngle);
		const largeArc = endAngle - startAngle > 180 ? 1 : 0;
		startAngle = endAngle;
		return (
			<path
				key={title}
				d={`M${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} L${ix1},${iy1} A${innerR},${innerR} 0 ${largeArc} 0 ${ix2},${iy2} Z`}
				fill={SERVICE_CHART_COLORS[i % SERVICE_CHART_COLORS.length]}
				stroke="var(--adm-surface)"
				strokeWidth="2"
			/>
		);
	});
};

const ServiceChart = ({ breakdown }) => {
	if (!breakdown.length) return <div className="adm-chart-empty">No data yet</div>;
	const total = breakdown.reduce((sum, [, count]) => sum + count, 0);
	return (
		<div className="adm-service-bars adm-service-bars--breakdown" role="list" aria-label={`${total} bookings across ${breakdown.length} services`}>
			{breakdown.map(([title, count], i) => {
				const share = Math.round((count / total) * 100);
				return (
					<div
						key={title}
						className="adm-service-bar-row"
						role="listitem"
						title={`${title}: ${count} bookings (${share}%)`}
						style={{ animationDelay: `${Math.min(i * 65, 390)}ms` }}
					>
						<span className="adm-service-bar-label">{title}</span>
						<div className="adm-service-bar-track" role="img" aria-label={`${share}% of bookings`}>
							<div
								className="adm-service-bar-fill"
								style={{ width: `${share}%`, background: SERVICE_CHART_COLORS[i % SERVICE_CHART_COLORS.length] }}
							/>
						</div>
						<span className="adm-service-bar-count">{count}</span>
						<span className="adm-service-bar-share">{share}%</span>
					</div>
				);
			})}
		</div>
	);
};

// ── Booking status: concentric "activity rings", animated on mount ───────────

const BOOKING_STATUS_RINGS = [
	{ id: "confirmed", label: "Confirmed", color: "#2d6a4f" },
	{ id: "pending", label: "Pending", color: "#a3690a" },
	{ id: "cancelled", label: "Cancelled", color: "#b9432f" },
];

const BookingStatusRings = ({ bookings }) => {
	const [animate, setAnimate] = useState(false);
	const total = bookings.length;

	useEffect(() => {
		setAnimate(false);
		const raf1 = requestAnimationFrame(() => {
			const raf2 = requestAnimationFrame(() => setAnimate(true));
			return () => cancelAnimationFrame(raf2);
		});
		return () => cancelAnimationFrame(raf1);
	}, [total]);

	if (!total) return <div className="adm-chart-empty">No data yet</div>;

	const counts = BOOKING_STATUS_RINGS.map((cfg) => bookings.filter((b) => (b.status || "pending") === cfg.id).length);
	const CX = 90, CY = 90, STROKE = 13;
	const radii = [70, 53, 36];

	return (
		<div className="adm-rings-wrap">
			<svg viewBox="0 0 180 180" width="180" height="180" aria-label="Booking status breakdown">
				{BOOKING_STATUS_RINGS.map((cfg, i) => {
					const r = radii[i];
					const circumference = 2 * Math.PI * r;
					const pct = total ? counts[i] / total : 0;
					const offset = circumference * (1 - pct);
					return (
						<g key={cfg.id} transform={`rotate(-90 ${CX} ${CY})`}>
							<circle cx={CX} cy={CY} r={r} fill="none" stroke="var(--adm-line)" strokeWidth={STROKE} />
							<circle
								cx={CX} cy={CY} r={r} fill="none"
								stroke={cfg.color} strokeWidth={STROKE} strokeLinecap="round"
								strokeDasharray={circumference}
								strokeDashoffset={animate ? offset : circumference}
								style={{ transition: `stroke-dashoffset 1s cubic-bezier(.16,1,.3,1) ${i * 150}ms` }}
							/>
						</g>
					);
				})}
				<text x={CX} y={CY - 4} textAnchor="middle" fontSize="26" fontWeight="700" fontFamily="var(--font-display)" fill="var(--adm-ink)">{total}</text>
				<text x={CX} y={CY + 16} textAnchor="middle" fontSize="11" fill="var(--adm-ink-faint)">this month</text>
			</svg>
			<div className="adm-rings-legend">
				{BOOKING_STATUS_RINGS.map((cfg, i) => (
					<div className="adm-legend-row" key={cfg.id}>
						<span className="adm-legend-dot" style={{ background: cfg.color }} />
						<span className="adm-legend-label">{cfg.label}</span>
						<span className="adm-legend-count">{counts[i]} · {total ? Math.round((counts[i] / total) * 100) : 0}%</span>
					</div>
				))}
			</div>
		</div>
	);
};

// ── Peak booking hours: which time slots fill up, animated bars ──────────────

function formatHourLabel(time) {
	const [h] = String(time).split(":").map(Number);
	if (Number.isNaN(h)) return time;
	const period = h >= 12 ? "pm" : "am";
	const hour12 = h % 12 === 0 ? 12 : h % 12;
	return `${hour12}${period}`;
}

const PeakHoursChart = ({ bookings }) => {
	const [animate, setAnimate] = useState(false);

	const slots = useMemo(() => {
		const counts = {};
		bookings.forEach((b) => { if (b.time) counts[b.time] = (counts[b.time] || 0) + 1; });
		return Object.keys(counts).sort().map((time) => ({ time, count: counts[time] }));
	}, [bookings]);

	useEffect(() => {
		setAnimate(false);
		const raf1 = requestAnimationFrame(() => {
			const raf2 = requestAnimationFrame(() => setAnimate(true));
			return () => cancelAnimationFrame(raf2);
		});
		return () => cancelAnimationFrame(raf1);
	}, [slots.length]);

	if (!slots.length) return <div className="adm-chart-empty">No data yet</div>;

	const max = Math.max(...slots.map((s) => s.count), 1);
	const busiest = slots.reduce((a, b) => (b.count > a.count ? b : a), slots[0]);

	return (
		<div className="adm-hours-chart-wrap">
			<div className="adm-hours-chart">
				{slots.map((s, i) => (
					<div className="adm-hours-chart__col" key={s.time} title={`${s.count} booking${s.count !== 1 ? "s" : ""} at ${formatHourLabel(s.time)}`}>
						<span className="adm-hours-chart__value">{s.count}</span>
						<div className="adm-hours-chart__track">
							<div
								className={`adm-hours-chart__bar${s.time === busiest.time ? " adm-hours-chart__bar--peak" : ""}`}
								style={{ height: animate ? `${Math.max((s.count / max) * 100, 6)}%` : "0%", transitionDelay: `${i * 45}ms` }}
							/>
						</div>
						<span className="adm-hours-chart__label">{formatHourLabel(s.time)}</span>
					</div>
				))}
			</div>
			<p className="adm-storage-panel__note">Busiest slot: <strong>{formatHourLabel(busiest.time)}</strong> with {busiest.count} booking{busiest.count !== 1 ? "s" : ""}</p>
		</div>
	);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent, icon: Icon, trend, index = 0, tint }) => (
	<div
		className={`adm-stat-card${accent ? " adm-stat-card--accent" : ""}${tint ? ` adm-stat-card--${tint === "amber" ? "warm" : tint}` : ""}`}
		style={{ animationDelay: `${index * 70}ms` }}
	>
		{Icon && (
			<span className={`adm-stat-card__icon-circle${tint ? ` adm-stat-card__icon-circle--${tint}` : ""}`}>
				<Icon size={19} strokeWidth={2.2} />
			</span>
		)}
		<div className="adm-stat-card__main">
			<div className="adm-stat-label">{label}</div>
			<div className="adm-stat-value-row">
				<span className="adm-stat-value">{value}</span>
				{trend != null && (
					<span className={`adm-stat-trend ${trend >= 0 ? "adm-stat-trend--good" : "adm-stat-trend--bad"}`}>
						{trend >= 0 ? <ArrowUp size={11} strokeWidth={3} /> : <ArrowDown size={11} strokeWidth={3} />}
						{Math.abs(trend)}%
					</span>
				)}
			</div>
			{sub && <div className="adm-stat-sub">{sub}</div>}
		</div>
	</div>
);

const BookingRow = ({ booking, onDelete, onQuickStatus, showDate = false, hideFinancialDetails = false, canViewMeetingLink = false }) => {
	const dot = SERVICE_COLORS[booking.serviceId] || "#888";
	const meetingUrl = canViewMeetingLink ? zoomJoinUrl(booking) : "";
	const [meetingCopied, setMeetingCopied] = useState(false);

	const copyMeetingLink = async () => {
		if (!meetingUrl || !navigator.clipboard?.writeText) return;
		try {
			await navigator.clipboard.writeText(meetingUrl);
			setMeetingCopied(true);
			window.setTimeout(() => setMeetingCopied(false), 1800);
		} catch (error) {
			console.error("Could not copy Zoom meeting link:", error);
		}
	};

	return (
		<div className="adm-booking-row">
			<div className="adm-booking-time">{booking.time}</div>
			<div className={`adm-customer-avatar adm-customer-avatar--${avatarColorFor(booking.customerName)}`}>
				{initialsFor(booking.name)}
			</div>
			<div className="adm-booking-info">

				<div className="adm-booking-name">
					<span className="adm-booking-dot" style={{ background: dot }} />
					{booking.name} {booking.surname}
				</div>
				<div className="adm-booking-meta">
					{showDate && <>{booking.date || "Date unavailable"} &middot; </>}
					{booking.serviceTitle} &middot; {booking.mode === "in-person" ? "In Person" : "Virtual"}
					{!hideFinancialDetails && booking.price && <> &middot; {booking.price}</>}
				</div>
				<div className="adm-booking-contact">
					<a href={`mailto:${booking.email}`}>{booking.email}</a>
					&ensp;·&ensp;{booking.cellphone}
				</div>
				{canViewMeetingLink && booking.mode === "virtual" && (
					<div className="adm-booking-meeting">
						{meetingUrl ? (
							<>
								<a href={meetingUrl} target="_blank" rel="noreferrer" className="adm-booking-meeting__open">
									<Video size={13} aria-hidden="true" /> Open Zoom meeting
								</a>
								<button type="button" className="adm-booking-meeting__copy" onClick={copyMeetingLink} title="Copy Zoom meeting link">
									{meetingCopied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
									{meetingCopied ? "Copied" : "Copy link"}
								</button>
							</>
						) : (
							<span className={`adm-booking-meeting__status${booking.zoomMeeting?.status === "failed" ? " adm-booking-meeting__status--error" : ""}`}>
								<Video size={13} aria-hidden="true" />
								{booking.zoomMeeting?.status === "failed" ? "Zoom link could not be created" : "Zoom link pending"}
							</span>
						)}
					</div>
				)}
			</div>

			<div className="adm-booking-row__actions">
				<select
					className="adm-booking-status-select"
					value={booking.status}
					onChange={(e) => onQuickStatus(booking, e.target.value)}
					data-status={booking.status}
					title="Change status"
				>
					{BOOKING_STATUS_OPTIONS.map((s) => (
						<option key={s.id} value={s.id}>{s.label}</option>
					))}
				</select>
				<button className="adm-icon-btn adm-icon-btn--danger" title="Delete booking" onClick={() => onDelete(booking)}>🗑</button>
			</div>
		</div>
	);
};

// ── Booking configuration panels (live in the Bookings tab) ──────────────────

function parseAdminAmount(value, label, { required = false } = {}) {
	const text = String(value ?? "").trim();
	if (!text) {
		if (required) throw new Error(`${label} is required. Enter an amount such as 850.`);
		return 0;
	}
	const match = text.match(/(?:R\s*)?([\d,]+(?:\.\d+)?)/i);
	const amount = match ? Number(match[1].replace(/,/g, "")) : NaN;
	if (!Number.isFinite(amount) || amount < 0) {
		throw new Error(`${label} must be a valid non-negative amount, such as 850.`);
	}
	return Math.round(amount * 100) / 100;
}

const WeekendAvailabilityPanel = ({ theme }) => {
	const [availability, setAvailability] = useState({
		saturday: false,
		sunday: false,
		weekdayTimes: ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"],
		saturdayTimes: ["09:00", "10:00", "11:00", "12:00"],
		sundayTimes: ["09:00", "10:00", "11:00", "12:00"],
	});
	const [blockedDates, setBlockedDates] = useState([]);
	const [blockedDate, setBlockedDate] = useState("");
	const [blockedSlots, setBlockedSlots] = useState([]);
	const [blockedTimeRanges, setBlockedTimeRanges] = useState([]);
	const [blockedRangeDate, setBlockedRangeDate] = useState("");
	const [blockedRangeStartTime, setBlockedRangeStartTime] = useState("");
	const [blockedRangeEndTime, setBlockedRangeEndTime] = useState("");
	const [blockTimeModalOpen, setBlockTimeModalOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [saved, setSaved] = useState(false);
	const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
	const [scheduleBackup, setScheduleBackup] = useState(null);

	useEffect(() => {
		getDoc(doc(db, "settings", "bookingAvailability"))
			.then((snap) => {
				if (snap.exists()) {
					const data = snap.data();
					const saturdayTimes = Array.isArray(data.saturdayTimes) ? data.saturdayTimes : ["09:00", "10:00", "11:00", "12:00"];
					setAvailability({
						saturday: data.saturday === true,
						sunday: data.sunday === true,
						weekdayTimes: Array.isArray(data.weekdayTimes) ? data.weekdayTimes : ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"],
						saturdayTimes,
						sundayTimes: Array.isArray(data.sundayTimes) ? data.sundayTimes : saturdayTimes,
					});
					setBlockedDates(Array.isArray(data.blockedDates) ? data.blockedDates.filter(Boolean).sort() : []);
					setBlockedSlots(Array.isArray(data.blockedSlots)
						? data.blockedSlots.filter((slot) => typeof slot === "string").sort()
						: []);
					setBlockedTimeRanges(Array.isArray(data.blockedTimeRanges)
						? data.blockedTimeRanges.filter((range) => (
							range
							&& typeof range.date === "string"
							&& typeof range.startTime === "string"
							&& typeof range.endTime === "string"
						)).sort((a, b) => `${a.date}_${a.startTime}`.localeCompare(`${b.date}_${b.startTime}`))
						: []);
				}
			})
			.catch((err) => setError(err.message || "Could not load weekend availability."))
			.finally(() => setLoading(false));
	}, []);

	const handleSave = async () => {
		setSaving(true);
		setError(null);
		try {
			const cleanAvailability = {
				...availability,
				weekdayTimes: availability.weekdayTimes.filter(Boolean),
				saturdayTimes: availability.saturdayTimes.filter(Boolean),
				sundayTimes: availability.sundayTimes.filter(Boolean),
			};
			await setDoc(doc(db, "settings", "bookingAvailability"), { ...cleanAvailability, blockedDates }, { merge: true });
			setAvailability(cleanAvailability);
			setSaved(true);
			setTimeout(() => setSaved(false), 2500);
			return true;
		} catch (err) {
			setError(err.message || "Could not save weekend availability.");
			return false;
		} finally {
			setSaving(false);
		}
	};

	const saveBlockedDates = async (nextDates) => {
		setSaving(true);
		setError(null);
		try {
			await setDoc(doc(db, "settings", "bookingAvailability"), { blockedDates: nextDates }, { merge: true });
			setSaved(true);
			setTimeout(() => setSaved(false), 2500);
		} catch (err) {
			setError(err.message || "Could not save blocked dates.");
		} finally {
			setSaving(false);
		}
	};

	const addBlockedDate = async () => {
		if (!blockedDate || blockedDates.includes(blockedDate)) return;
		const nextDates = [...blockedDates, blockedDate].sort();
		setBlockedDates(nextDates);
		setBlockedDate("");
		await saveBlockedDates(nextDates);
	};

	const removeBlockedDate = async (date) => {
		const nextDates = blockedDates.filter((item) => item !== date);
		setBlockedDates(nextDates);
		await saveBlockedDates(nextDates);
	};

	const saveBlockedSlots = async (nextSlots) => {
		setSaving(true);
		setError(null);
		try {
			await setDoc(doc(db, "settings", "bookingAvailability"), { blockedSlots: nextSlots }, { merge: true });
			setSaved(true);
			setTimeout(() => setSaved(false), 2500);
		} catch (err) {
			setError(err.message || "Could not save blocked times.");
		} finally {
			setSaving(false);
		}
	};

	const saveBlockedTimeRanges = async (nextRanges) => {
		setSaving(true);
		setError(null);
		try {
			await setDoc(doc(db, "settings", "bookingAvailability"), { blockedTimeRanges: nextRanges }, { merge: true });
			setSaved(true);
			setTimeout(() => setSaved(false), 2500);
		} catch (err) {
			setError(err.message || "Could not save blocked time ranges.");
		} finally {
			setSaving(false);
		}
	};

	const addBlockedTimeRange = async () => {
		if (!blockedRangeDate || !blockedRangeStartTime || !blockedRangeEndTime) return;
		if (blockedRangeStartTime >= blockedRangeEndTime) {
			setError("End time must be later than start time.");
			return;
		}
		const range = {
			date: blockedRangeDate,
			startTime: blockedRangeStartTime,
			endTime: blockedRangeEndTime,
		};
		if (blockedTimeRanges.some((item) => (
			item.date === range.date
			&& item.startTime === range.startTime
			&& item.endTime === range.endTime
		))) return;
		const nextRanges = [...blockedTimeRanges, range].sort((a, b) => (
			`${a.date}_${a.startTime}`.localeCompare(`${b.date}_${b.startTime}`)
		));
		setBlockedTimeRanges(nextRanges);
		setBlockedRangeDate("");
		setBlockedRangeStartTime("");
		setBlockedRangeEndTime("");
		await saveBlockedTimeRanges(nextRanges);
	};

	const removeBlockedSlot = async (slot) => {
		const nextSlots = blockedSlots.filter((item) => item !== slot);
		setBlockedSlots(nextSlots);
		await saveBlockedSlots(nextSlots);
	};

	const removeBlockedTimeRange = async (rangeToRemove) => {
		const nextRanges = blockedTimeRanges.filter((range) => range !== rangeToRemove);
		setBlockedTimeRanges(nextRanges);
		await saveBlockedTimeRanges(nextRanges);
	};

	const closeBlockTimeModal = useCallback(() => setBlockTimeModalOpen(false), []);

	useEffect(() => {
		if (!blockTimeModalOpen) return undefined;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const handleKeyDown = (event) => {
			if (event.key === "Escape") closeBlockTimeModal();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [blockTimeModalOpen, closeBlockTimeModal]);

	const updateTimes = (schedule, index, value) => {
		setAvailability((current) => ({
			...current,
			[schedule]: current[schedule].map((time, timeIndex) => timeIndex === index ? value : time),
		}));
	};

	const addTime = (schedule) => {
		setAvailability((current) => ({ ...current, [schedule]: [...current[schedule], ""] }));
	};

	const removeTime = (schedule, index) => {
		setAvailability((current) => ({
			...current,
			[schedule]: current[schedule].filter((_, timeIndex) => timeIndex !== index),
		}));
	};

	const openScheduleEditor = () => {
		setScheduleBackup({
			weekdayTimes: [...availability.weekdayTimes],
			saturdayTimes: [...availability.saturdayTimes],
			sundayTimes: [...availability.sundayTimes],
		});
		setScheduleEditorOpen(true);
	};

	const closeScheduleEditor = useCallback(() => {
		if (scheduleBackup) setAvailability((current) => ({ ...current, ...scheduleBackup }));
		setScheduleEditorOpen(false);
		setScheduleBackup(null);
	}, [scheduleBackup]);

	useEffect(() => {
		if (!scheduleEditorOpen) return undefined;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const handleKeyDown = (event) => {
			if (event.key === "Escape") closeScheduleEditor();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [scheduleEditorOpen, closeScheduleEditor]);

	const saveSchedule = async () => {
		const didSave = await handleSave();
		if (didSave) {
			setScheduleEditorOpen(false);
			setScheduleBackup(null);
		}
	};

	if (loading) return <div className="adm-bookings-empty">Loading booking availability…</div>;

	return (
		<div className="adm-settings-panel">
			<div className="adm-settings-panel__title">Booking availability</div>
			<p className="adm-settings-panel__note">Set the appointment times clients can choose. Weekends are closed by default.</p>
			{error && <div className="adm-query-error" role="alert">{error}</div>}
			<div className="adm-settings-panel__toggles">
				{[["saturday", "Saturday"], ["sunday", "Sunday"]].map(([id, label]) => (
					<label className="adm-toggle-row" key={id}>
						<span>{label}</span>
						<span className="adm-toggle">
							<input
								type="checkbox"
								checked={availability[id]}
								onChange={(event) => setAvailability((current) => ({ ...current, [id]: event.target.checked }))}
							/>
							<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
						</span>
					</label>
				))}
			</div>
			<div className="adm-schedule-summary">
				<div>
					<strong>Appointment times</strong>
					<span>Weekdays {availability.weekdayTimes[0] || "Closed"}–{availability.weekdayTimes[availability.weekdayTimes.length - 1] || ""} · Saturday last start {availability.saturdayTimes[availability.saturdayTimes.length - 1] || "closed"}</span>
				</div>
				<button type="button" className="adm-edit-hours-btn" onClick={openScheduleEditor}>
					<Settings2 size={16} aria-hidden="true" />
					<span>Edit hours</span>
				</button>
			</div>
			<div className="adm-blocked-dates">
				<div>
					<div className="adm-blocked-dates__label">Blocked dates and times</div>
					<p className="adm-settings-panel__note">Manage dates and time ranges when appointments are unavailable.</p>
				</div>
				<button type="button" className="adm-edit-hours-btn" onClick={() => setBlockTimeModalOpen(true)}>
					<CalendarClock size={16} aria-hidden="true" />
					<span>Manage blocked dates & times</span>
				</button>
			</div>
			<div className="adm-settings-panel__actions">
				<button className="adm-primary-btn" onClick={handleSave} disabled={saving}>
					{saving ? "Saving…" : "Save availability"}
				</button>
				{saved && <span className="adm-settings-panel__saved">Saved ✓</span>}
			</div>
				{blockTimeModalOpen && createPortal(
					<div className="adm-page adm-hours-portal" data-theme={theme}>
						<div className="adm-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeBlockTimeModal(); }}>
							<div className="adm-hours-modal adm-block-time-modal" role="dialog" aria-modal="true" aria-labelledby="block-time-modal-title">
								<div className="adm-hours-modal__header">
									<div>
										<div className="adm-modal-eyebrow">Booking availability</div>
										<h3 id="block-time-modal-title">Block dates and times</h3>
										<p>Choose a full day or block a specific time range. Changes apply immediately.</p>
									</div>
									<button type="button" className="adm-modal-close" onClick={closeBlockTimeModal} aria-label="Close blocked dates and times">
										<X size={18} aria-hidden="true" />
									</button>
								</div>
								<div className="adm-hours-modal__body adm-block-time-modal__body">
									{error && <div className="adm-query-error" role="alert">{error}</div>}
									<section className="adm-block-time-section" aria-labelledby="block-date-heading">
										<div className="adm-block-time-section__heading">
											<strong id="block-date-heading">Block an entire day</strong>
											<span>No appointments will be available on this date.</span>
										</div>
										<div className="adm-block-time-form">
											<label>
												<span>Date</span>
												<input
													type="date"
													value={blockedDate}
													min={toISODate(new Date())}
													onChange={(event) => setBlockedDate(event.target.value)}
												/>
											</label>
											<button type="button" className="adm-secondary-btn" onClick={addBlockedDate} disabled={!blockedDate || saving}>
												Block full day
											</button>
										</div>
										{blockedDates.length > 0 && (
											<div className="adm-blocked-dates__list">
												{blockedDates.map((date) => (
													<div className="adm-blocked-date" key={date}>
														<span>{formatDisplayDate(date)}</span>
														<button type="button" onClick={() => removeBlockedDate(date)} aria-label={`Unblock ${formatDisplayDate(date)}`} disabled={saving}>Remove</button>
													</div>
												))}
											</div>
										)}
									</section>
									<section className="adm-block-time-section" aria-labelledby="block-range-heading">
										<div className="adm-block-time-section__heading">
											<strong id="block-range-heading">Block a time range</strong>
											<span>Appointments starting at or after the start time and before the end time will be unavailable.</span>
										</div>
										<div className="adm-block-time-form adm-block-time-form--range">
											<label>
												<span>Date</span>
												<input
													type="date"
													value={blockedRangeDate}
													min={toISODate(new Date())}
													onChange={(event) => setBlockedRangeDate(event.target.value)}
												/>
											</label>
											<label>
												<span>Start time</span>
												<input
													type="time"
													value={blockedRangeStartTime}
													onChange={(event) => setBlockedRangeStartTime(event.target.value)}
												/>
											</label>
											<label>
												<span>End time</span>
												<input
													type="time"
													value={blockedRangeEndTime}
													onChange={(event) => setBlockedRangeEndTime(event.target.value)}
												/>
											</label>
											<button type="button" className="adm-secondary-btn" onClick={addBlockedTimeRange} disabled={!blockedRangeDate || !blockedRangeStartTime || !blockedRangeEndTime || saving}>
												Block time range
											</button>
										</div>
										{blockedTimeRanges.length > 0 && (
											<div className="adm-blocked-dates__list">
												{blockedTimeRanges.map((range) => (
													<div className="adm-blocked-date" key={`${range.date}_${range.startTime}_${range.endTime}`}>
														<span>{formatDisplayDate(range.date)} · {range.startTime}–{range.endTime}</span>
														<button type="button" onClick={() => removeBlockedTimeRange(range)} aria-label={`Unblock ${formatDisplayDate(range.date)} from ${range.startTime} to ${range.endTime}`} disabled={saving}>Remove</button>
													</div>
												))}
											</div>
										)}
									</section>
									{blockedSlots.length > 0 && (
										<section className="adm-block-time-section" aria-labelledby="legacy-blocked-times-heading">
											<div className="adm-block-time-section__heading">
												<strong id="legacy-blocked-times-heading">Previously blocked individual times</strong>
												<span>These existing blocks remain active until removed.</span>
											</div>
											<div className="adm-blocked-dates__list">
												{blockedSlots.map((slot) => {
													const date = slot.slice(0, 10);
													const time = slot.slice(11);
													return (
														<div className="adm-blocked-date" key={slot}>
															<span>{formatDisplayDate(date)} at {time}</span>
															<button type="button" onClick={() => removeBlockedSlot(slot)} aria-label={`Unblock ${formatDisplayDate(date)} at ${time}`} disabled={saving}>Remove</button>
														</div>
													);
												})}
											</div>
										</section>
									)}
								</div>
								<div className="adm-hours-modal__footer">
									{saved && <span className="adm-settings-panel__saved">Saved ✓</span>}
									<button type="button" className="adm-secondary-btn" onClick={closeBlockTimeModal}>Done</button>
								</div>
							</div>
						</div>
					</div>,
					document.body
				)}
				{scheduleEditorOpen && createPortal(
				<div className="adm-page adm-hours-portal" data-theme={theme}>
				<div className="adm-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeScheduleEditor(); }}>
					<div className="adm-hours-modal" role="dialog" aria-modal="true" aria-labelledby="hours-modal-title">
						<div className="adm-hours-modal__header">
							<div>
								<div className="adm-modal-eyebrow">Booking settings</div>
								<h3 id="hours-modal-title">Edit appointment hours</h3>
								<p>Appointments can start as late as 17:00 in person or 18:00 online.</p>
							</div>
							<button type="button" className="adm-modal-close" onClick={closeScheduleEditor} aria-label="Close appointment hours editor">
								<X size={18} aria-hidden="true" />
							</button>
						</div>
						<div className="adm-hours-modal__body">
							{[
								["weekdayTimes", "Weekdays", "Monday to Friday"],
								["saturdayTimes", "Saturday", "Optional weekend hours"],
								["sundayTimes", "Sunday", "Optional weekend hours"],
							].map(([schedule, label, description]) => (
								<div className="adm-hours-group" key={schedule}>
									<div className="adm-hours-group__heading">
										<div><strong>{label}</strong><span>{description}</span></div>
										<button type="button" className="adm-add-time-btn" onClick={() => addTime(schedule)}><Plus size={14} aria-hidden="true" /> Add time</button>
									</div>
									<div className="adm-hours-list">
										{availability[schedule].map((time, index) => (
											<div className="adm-hours-row" key={`${schedule}-${index}`}>
												<Clock size={16} aria-hidden="true" />
												<input type="time" value={time} onChange={(event) => updateTimes(schedule, index, event.target.value)} aria-label={`${label} time ${index + 1}`} />
												<button type="button" className="adm-text-btn adm-text-btn--danger" onClick={() => removeTime(schedule, index)} disabled={availability[schedule].length <= 1}>Remove</button>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
						<div className="adm-hours-modal__footer">
							<button type="button" className="adm-secondary-btn" onClick={closeScheduleEditor}>Cancel</button>
							<button type="button" className="adm-primary-btn" onClick={saveSchedule} disabled={saving}>{saving ? "Saving…" : "Save hours"}</button>
						</div>
					</div>
				</div>
				</div>,
				document.body
			)}
		</div>
	);
};

// ── Google Calendar connection (lives inside Bookings) ───────────────────────

const CalendarSyncTrigger = () => {
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(true);
	const [connecting, setConnecting] = useState(false);
	const [disconnectingId, setDisconnectingId] = useState(null);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);

	const loadStatus = async () => {
		setLoading(true);
		try {
			setStatus(await calendarApiRequest("status"));
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get("calendar") === "connected") {
			const synced = params.get("synced");
			setNotice(`Google Calendar connected${synced ? ` — ${synced} future booking${synced === "1" ? "" : "s"} imported` : ""}.`);
			window.history.replaceState({}, document.title, window.location.pathname);
		} else if (params.get("calendar") === "error") {
			setError(params.get("calendarMessage") || "Google Calendar could not be connected.");
			window.history.replaceState({}, document.title, window.location.pathname);
		}
		loadStatus();
	}, []);

	const handleConnect = async () => {
		setConnecting(true);
		setError(null);
		try {
			const { authorizationUrl } = await calendarApiRequest("connect");
			window.location.assign(authorizationUrl);
		} catch (err) {
			setError(err.message);
			setConnecting(false);
		}
	};

	const handleDisconnect = async (account) => {
		if (!window.confirm(`Remove ${account.email} from the live booking feed? Website-created booking events will also be removed from that Google Calendar.`)) return;
		setDisconnectingId(account.id);
		setError(null);
		try {
			const result = await calendarApiRequest("disconnect", { googleAccountId: account.id });
			setStatus((current) => {
				const accounts = (current?.accounts || []).filter((item) => item.id !== account.id);
				return { connected: accounts.length > 0, accounts };
			});
			setNotice(`${account.email} no longer receives the live feed${result.removedEvents ? ` — ${result.removedEvents} booking event${result.removedEvents === 1 ? "" : "s"} removed` : ""}.`);
			if (result.failures?.length) {
				setError("The account was removed, but some old calendar events could not be removed automatically.");
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setDisconnectingId(null);
		}
	};

	const accounts = status?.accounts || [];

	return (
		<div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--adm-line)" }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
				<div>
					<div style={{ fontWeight: 650, fontSize: 13.5 }}>Google Calendar live feeds</div>
					<div style={{ marginTop: 3, fontSize: 12.5, color: "var(--adm-ink-soft)", lineHeight: 1.45 }}>
						Choose exactly which Google Calendar accounts receive bookings on their phones.
					</div>
				</div>
				{!loading && (
					<button type="button" className="adm-primary-btn" onClick={handleConnect} disabled={connecting} style={{ whiteSpace: "nowrap" }}>
						{connecting ? "Opening Google…" : accounts.length ? "Add account" : "Connect Google"}
					</button>
				)}
			</div>

			{error && <div className="adm-query-error" style={{ marginTop: 12 }}>{error}</div>}
			{notice && <div className="adm-user-form__success" role="status" style={{ marginTop: 12 }}><Check size={15} aria-hidden="true" /> {notice}</div>}

			{loading ? (
				<div style={{ marginTop: 10, fontSize: 12.5, color: "var(--adm-ink-faint)" }}>Checking calendar connection…</div>
			) : accounts.length ? (
				<div style={{ marginTop: 12, fontSize: 12.5, color: "var(--adm-ink-soft)", lineHeight: 1.5 }}>
					<div style={{ marginBottom: 8 }}>New, changed, cancelled, and deleted website bookings update automatically in these calendars:</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
						{accounts.map((account) => (
							<div key={account.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", padding: "8px 10px", border: "1px solid var(--adm-line)", borderRadius: 8, background: "var(--adm-surface-raised)" }}>
								<span style={{ color: "var(--adm-ink)", overflowWrap: "anywhere" }}>{account.email}</span>
								<button type="button" className="adm-text-btn" onClick={() => handleDisconnect(account)} disabled={disconnectingId === account.id} style={{ whiteSpace: "nowrap", color: "var(--adm-danger, #b42318)" }}>
									{disconnectingId === account.id ? "Removing…" : "Remove"}
								</button>
							</div>
						))}
					</div>
				</div>
			) : (
				<div style={{ marginTop: 12, fontSize: 12.5, color: "var(--adm-ink-soft)", lineHeight: 1.5 }}>
					Choose <strong style={{ color: "var(--adm-ink)" }}>Connect Google</strong>, sign in with the Gmail account whose calendar should receive bookings, and approve access. You can add more accounts later or remove one to stop its live feed.
				</div>
			)}
		</div>
	);
};

// ── Books Tab ─────────────────────────────────────────────────────────────────

// ── Books Tab ─────────────────────────────────────────────────────────────────

const BooksTab = ({ db }) => {
	const [view, setView] = useState("catalog"); // catalog | orders
	const [books, setBooks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setProgress] = useState(0);
	const [pdfProgress, setPdfProgress] = useState(0);
	const [error, setError] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editBook, setEditBook] = useState(null);

	// Neither format defaults to "on" — a new book starts with no format
	// selected so the admin has to actively choose hard copy, PDF, or both,
	// instead of PDF silently being required because its toggle defaulted
	// true and nobody noticed it needed switching off.
	const [form, setForm] = useState({
		title: "", author: "", description: "", price: "",
		currency: "ZAR", available: true,
		physicalAvailable: false, physicalPrice: "", physicalStock: "",
		digitalAvailable: false,
	});
	const [coverFile, setCoverFile] = useState(null);
	const [coverPreview, setCoverPreview] = useState(null);
	const fileRef = useRef();

	const [pdfFile, setPdfFile] = useState(null);
	const [pdfName, setPdfName] = useState(null);
	const pdfRef = useRef();

	useEffect(() => {
		const unsub = onSnapshot(collection(db, "books"), (snap) => {
			setBooks(snap.docs.map(d => ({ id: d.id, ...d.data() }))
				.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
			setLoading(false);
		}, (err) => { setError(err.message); setLoading(false); });
		return unsub;
	}, [db]);

	const resetForm = () => {
		setForm({
			title: "", author: "", description: "", price: "", currency: "ZAR", available: true,
			physicalAvailable: false, physicalPrice: "", physicalStock: "",
			digitalAvailable: false,
		});
		setCoverFile(null); setCoverPreview(null);
		setPdfFile(null); setPdfName(null);
		setEditBook(null); setShowForm(false);
	};

	const openEdit = (book) => {
		setEditBook(book);
		setForm({
			title: book.title || "", author: book.author || "",
			description: book.description || "", price: book.price || "",
			currency: book.currency || "ZAR",
			available: book.available !== false,
			physicalAvailable: book.physicalAvailable === true,
			physicalPrice: book.physicalPrice || book.price || "",
			physicalStock: Number.isInteger(book.physicalStock) ? String(book.physicalStock) : "",
			// digitalAvailable/physicalAvailable are read strictly: a book saved
			// before these toggles existed shows both as OFF here rather than
			// guessing from a leftover pdfUrl. That's deliberate — silently
			// inferring "on" meant the PDF toggle could pre-check itself for a
			// book you now intend to sell as hard-copy-only, with nothing in the
			// UI flagging that it happened. Re-check the toggles you actually
			// want and save once; that writes the explicit flags going forward.
			digitalAvailable: book.digitalAvailable === true,
		});
		setCoverPreview(book.coverUrl || null);
		setCoverFile(null);
		setPdfName(book.pdfFileName || (book.pdfUrl ? "Existing PDF" : null));
		setPdfFile(null);
		setShowForm(true);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setCoverFile(file);
		setCoverPreview(URL.createObjectURL(file));
	};

	const handlePdfChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		if (file.type !== "application/pdf") {
			setError("Please choose a PDF file.");
			return;
		}
		setError(null);
		setPdfFile(file);
		setPdfName(file.name);
	};

	const handleSubmit = async () => {
		if (!form.title.trim()) { setError("Title is required."); return; }
		if (!form.physicalAvailable && !form.digitalAvailable) {
			setError("Choose at least one format: hard copy or PDF.");
			return;
		}
		const hasExistingPdf = Boolean(editBook?.pdfUrl);
		if (form.digitalAvailable && !pdfFile && !hasExistingPdf) {
			setError("Please upload the book's PDF file, or turn off 'Available as PDF'.");
			return;
		}
		setError(null);
		setUploading(true);
		setProgress(0);
		setPdfProgress(0);

		try {
			let coverUrl = editBook?.coverUrl || "";
			let coverPublicId = editBook?.coverPublicId || "";
			let pdfUrl = editBook?.pdfUrl || "";
			let pdfPublicId = editBook?.pdfPublicId || "";
			let pdfFileName = editBook?.pdfFileName || "";

			// Cover image now goes to Supabase Storage (bucket: "book-covers")
			// instead of Cloudinary. The old asset (if any) is removed from
			// Supabase on replace. If an older book still has a Cloudinary
			// coverPublicId from before this change, that ID simply won't match
			// anything in the Supabase bucket and the delete call is a silent
			// no-op — same practical effect as before, since Cloudinary
			// deletion was already a no-op on the unsigned client-side path.
			if (coverFile) {
				const result = await supabaseUploadImage(coverFile, setProgress);
				coverUrl = result.url;
				coverPublicId = result.path;
				if (editBook?.coverPublicId) await supabaseDeleteImage(editBook.coverPublicId);
			}

			if (form.digitalAvailable && pdfFile) {
				const result = await supabaseUploadPdf(pdfFile, setPdfProgress);
				pdfUrl = result.url;
				pdfPublicId = result.path; // reuse the same Firestore field, now stores the Supabase path
				pdfFileName = pdfFile.name;
			}

			const bookPrice = form.digitalAvailable
				? parseAdminAmount(form.price, "PDF price", { required: true })
				: 0;
			const physicalPrice = form.physicalAvailable
				? parseAdminAmount(form.physicalPrice || form.price, "Hard copy price", { required: true })
				: 0;
			const stockValue = String(form.physicalStock).trim();
			const physicalStock = stockValue === "" ? null : Number(stockValue);
			if (physicalStock !== null && (!Number.isInteger(physicalStock) || physicalStock < 0)) {
				throw new Error("Hard copy stock must be a whole number of 0 or more.");
			}
			const payload = {
				title: form.title,
				author: form.author,
				description: form.description,
				price: bookPrice,
				currency: form.currency,
				available: form.available,
				physicalAvailable: form.physicalAvailable,
				physicalPrice,
				physicalStock,
				coverUrl,
				coverPublicId,
				digitalAvailable: form.digitalAvailable,
				// Clearing these when PDF is turned off means checkout can never
				// sell a format the admin has disabled, even if a file was
				// uploaded previously.
				pdfUrl: form.digitalAvailable ? pdfUrl : "",
				pdfPublicId: form.digitalAvailable ? pdfPublicId : "",
				pdfFileName: form.digitalAvailable ? pdfFileName : "",
				updatedAt: serverTimestamp(),
			};

			if (editBook) {
				await updateDoc(doc(db, "books", editBook.id), payload);
			} else {
				await addDoc(collection(db, "books"), { ...payload, createdAt: serverTimestamp() });
			}
			resetForm();
		} catch (err) {
			setError(err.message);
		} finally {
			setUploading(false);
			setProgress(0);
			setPdfProgress(0);
		}
	};

	const handleDelete = async (book) => {
		if (!window.confirm(`Delete "${book.title}"? This cannot be undone.`)) return;
		try {
			await deleteDoc(doc(db, "books", book.id));
			if (book.coverPublicId) await supabaseDeleteImage(book.coverPublicId);
			if (book.pdfPublicId) await supabaseDeletePdf(book.pdfPublicId);
		} catch (err) { setError(err.message); }
	};

	return (
		<div className="adm-books-tab">
			{error && <div className="adm-query-error">{error}</div>}

			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">Books</div>
					<div className="adm-books-sub">{books.length} title{books.length !== 1 ? "s" : ""} listed</div>
				</div>
				<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
					<div className="adm-commercial__tabs" role="tablist" aria-label="Books view">
						<button type="button" role="tab" aria-selected={view === "catalog"} className={view === "catalog" ? "is-active" : ""} onClick={() => setView("catalog")}>
							<BookOpen size={16} /> Catalog
						</button>
						<button type="button" role="tab" aria-selected={view === "orders"} className={view === "orders" ? "is-active" : ""} onClick={() => setView("orders")}>
							<Inbox size={16} /> Orders
						</button>
					</div>
					{view === "catalog" && (
						<button className="adm-primary-btn" onClick={() => { resetForm(); setShowForm(true); }}>
							+ Add book
						</button>
					)}
				</div>
			</div>

			{view === "orders" && <BookOrdersPanel db={db} />}

			{/* ── Upload / Edit Form ── */}
			{view === "catalog" && showForm && (
				<div className="adm-book-form">
					<div className="adm-book-form__title">{editBook ? "Edit book" : "Add new book"}</div>

					<div className="adm-book-form__grid">
						{/* Cover upload */}
						<div
							className="adm-cover-upload"
							onClick={() => fileRef.current?.click()}
							style={coverPreview ? { backgroundImage: `url(${coverPreview})` } : {}}
						>
							{!coverPreview && (
								<div className="adm-cover-upload__placeholder">
									<span className="adm-cover-upload__icon">📷</span>
									<span>Upload cover</span>
								</div>
							)}
							<input
								ref={fileRef}
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={handleFileChange}
							/>
						</div>

						{/* Fields */}
						<div className="adm-book-form__fields">
							<div className="adm-field-row">
								<label className="adm-field-label">Title *</label>
								<input
									className="adm-field-input"
									value={form.title}
									onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
									placeholder="Book title"
								/>
							</div>
							<div className="adm-field-row">
								<label className="adm-field-label">Author</label>
								<input
									className="adm-field-input"
									value={form.author}
									onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
									placeholder="Author name"
								/>
							</div>
							<div className="adm-field-row">
								<label className="adm-field-label">Description</label>
								<textarea
									className="adm-field-input adm-field-textarea"
									value={form.description}
									onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
									placeholder="Short description shown to buyers"
									rows={3}
								/>
							</div>

							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Available?</label>
									<div className="adm-toggle-row">
										<label className="adm-toggle">
											<input
												type="checkbox"
												checked={form.available}
												onChange={e => setForm(f => ({ ...f, available: e.target.checked }))}
											/>
											<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
										</label>
										<span className="adm-toggle-label">{form.available ? "Yes" : "No"}</span>
									</div>
									<small className="adm-field-hint">Turns the whole book listing on or off, regardless of format.</small>
								</div>
							</div>

							{/* ── PDF format ── */}
							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">PDF available?</label>
									<div className="adm-toggle-row">
										<label className="adm-toggle">
											<input
												type="checkbox"
												checked={form.digitalAvailable}
												onChange={e => {
													const checked = e.target.checked;
													setForm(f => ({ ...f, digitalAvailable: checked, price: checked ? f.price : "" }));
													// Leave off = nothing to upload for this format, so drop
													// whatever file was staged instead of silently discarding
													// it at submit time.
													if (!checked) {
														setPdfFile(null);
														setPdfName(null);
													}
												}}
											/>
											<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
										</label>
										<span className="adm-toggle-label">{form.digitalAvailable ? "Yes" : "No"}</span>
									</div>
									<small className="adm-field-hint">Off by default — turn this on only if you're also selling a PDF. It won't require a hard copy.</small>
								</div>
								{form.digitalAvailable && (
									<div>
										<label className="adm-field-label">PDF price (ZAR) — R 850 format *</label>
										<input
											className="adm-field-input"
											type="text"
											inputMode="decimal"
											min="0"
											step="1"
											value={form.price}
											onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
											placeholder="R 250"
										/>
									</div>
								)}
							</div>

							{/* PDF upload field — only shown when PDF is offered for this book */}
							{form.digitalAvailable && (
								<div className="adm-field-row">
									<label className="adm-field-label">Book PDF *</label>
									<div className="adm-pdf-upload" onClick={() => pdfRef.current?.click()}>
										<span className="adm-pdf-upload__icon">📄</span>
										<span className="adm-pdf-upload__text">
											{pdfName ? pdfName : "Click to choose a PDF file"}
										</span>
										<span className="adm-pdf-upload__action">
											{pdfName ? "Replace" : "Browse"}
										</span>
									</div>
									<input
										ref={pdfRef}
										type="file"
										accept="application/pdf"
										style={{ display: "none" }}
										onChange={handlePdfChange}
									/>
								</div>
							)}

							{/* ── Hard copy format ── */}
							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Hard copy available?</label>
									<div className="adm-toggle-row">
										<label className="adm-toggle">
											<input
												type="checkbox"
												checked={form.physicalAvailable}
												onChange={e => {
													const checked = e.target.checked;
													setForm(f => ({
														...f,
														physicalAvailable: checked,
														physicalPrice: checked ? f.physicalPrice : "",
														physicalStock: checked ? f.physicalStock : "",
													}));
												}}
											/>
											<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
										</label>
										<span className="adm-toggle-label">{form.physicalAvailable ? "Yes" : "No"}</span>
									</div>
									<small className="adm-field-hint">Off by default — turn this on only if you're also selling a hard copy. It won't require a PDF.</small>
								</div>
								{form.physicalAvailable && (
									<div>
										<label className="adm-field-label">Hard copy price (ZAR)</label>
										<input className="adm-field-input" type="text" inputMode="decimal" value={form.physicalPrice} onChange={e => setForm(f => ({ ...f, physicalPrice: e.target.value }))} placeholder="Same as PDF price" />
										<small className="adm-field-hint">Can differ from the PDF price and may include delivery.</small>
									</div>
								)}
							</div>
							{form.physicalAvailable && (
								<div className="adm-field-row adm-field-row--half">
									<div>
										<label className="adm-field-label">Hard copy stock</label>
										<input className="adm-field-input" type="number" min="0" step="1" value={form.physicalStock} onChange={e => setForm(f => ({ ...f, physicalStock: e.target.value }))} placeholder="Leave blank for unlimited" />
										<small className="adm-field-hint">Set 0 to show the hard copy as sold out.</small>
									</div>
								</div>
							)}
						</div>
					</div>

					{uploading && (
						<>
							{coverFile && (
								<div className="adm-upload-progress">
									<div className="adm-upload-progress__bar" style={{ width: `${uploadProgress}%` }} />
									<span>Cover {uploadProgress}%</span>
								</div>
							)}
							{pdfFile && (
								<div className="adm-upload-progress">
									<div className="adm-upload-progress__bar" style={{ width: `${pdfProgress}%` }} />
									<span>PDF {pdfProgress}%</span>
								</div>
							)}
						</>
					)}

					<div className="adm-book-form__actions">
						<button className="adm-ghost-btn" onClick={resetForm} disabled={uploading}>Cancel</button>
						<button className="adm-primary-btn" onClick={handleSubmit} disabled={uploading}>
							{uploading ? "Saving…" : editBook ? "Save changes" : "Publish book"}
						</button>
					</div>
				</div>
			)}

			{/* ── Book list ── */}
			{view === "catalog" && (loading ? (
				<div className="adm-bookings-empty">Loading books…</div>
			) : books.length === 0 ? (
				<div className="adm-bookings-empty">
					<div className="adm-bookings-empty__icon">📚</div>
					<div>No books yet. Add your first title above.</div>
				</div>
			) : (
				<div className="adm-book-list">
					{books.map(book => (
						<div key={book.id} className={`adm-book-card${!book.available ? " adm-book-card--unavailable" : ""}`}>
							{book.coverUrl
								? <img className="adm-book-card__cover" src={book.coverUrl} alt={book.title} />
								: <div className="adm-book-card__cover adm-book-card__cover--placeholder">📖</div>
							}
							<div className="adm-book-card__body">
								<div className="adm-book-card__title">{book.title}</div>
								{book.author && <div className="adm-book-card__author">{book.author}</div>}
								{book.description && <div className="adm-book-card__desc">{book.description}</div>}
								<div className="adm-book-card__footer">
									{!book.available && <span className="adm-badge adm-badge--muted">Unlisted</span>}
									<span className={`adm-badge ${book.digitalAvailable === true ? "adm-badge--blue" : "adm-badge--muted"}`}>
										PDF: {book.digitalAvailable === true ? (book.pdfUrl ? formatCurrency(book.price) : "no file") : "off"}
									</span>
									<span className={`adm-badge ${book.physicalAvailable === true ? "adm-badge--blue" : "adm-badge--muted"}`}>
										Hard copy: {book.physicalAvailable === true ? (book.physicalStock === 0 ? "sold out" : book.physicalStock == null ? "unlimited" : `${book.physicalStock} in stock`) : "off"}
									</span>
									{/* Neither flag is explicitly true — this book predates the format
									    toggles (or was written outside this form) and the storefront will
									    now show it as unavailable until you open it, confirm the formats
									    you actually want, and save once. */}
									{book.digitalAvailable !== true && book.physicalAvailable !== true && (
										<span className="adm-badge adm-badge--amber">⚠ Needs review — open &amp; re-save</span>
									)}
								</div>
							</div>
							<div className="adm-book-card__actions">
								<button className="adm-icon-btn" title="Edit" onClick={() => openEdit(book)}>✏️</button>
								<button className="adm-icon-btn adm-icon-btn--danger" title="Delete" onClick={() => handleDelete(book)}>🗑</button>
							</div>
						</div>
					))}
				</div>
			))}
		</div>
	);
};

// ── Book Orders (log of every checkout submitted for a book) ────────────────
// Streams the "bookOrders" collection in real time so the admin can see —
// per purchase, regardless of status — exactly what the buyer submitted:
// name, email, and (for hard copy only) cellphone + delivery address.
// Complements CommercialPanel's monthly revenue view below by giving a
// single, always-current, unfiltered-by-month log scoped to books.

const ORDER_STATUS_LABELS = {
	pending: "Pending payment",
	processing: "Processing",
	paid: "Paid",
	failed: "Failed",
};

const BOOK_DELIVERY_FOLLOW_UP_MS = 2 * 24 * 60 * 60 * 1000;

function orderMoney(order) {
	const currency = order.currency || "ZAR";
	const amount = Number(order.amountCents || 0) / 100;
	return new Intl.NumberFormat("en-ZA", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
}

// "12 Sep 2026, 14:32". Firestore serverTimestamp fields are empty until the
// write is confirmed, hence the "Just now" fallback.
function orderWhen(createdAt) {
	const date = createdAt?.toDate ? createdAt.toDate() : null;
	if (!date) return "Just now";
	const day = date.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
	const time = date.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
	return `${day}, ${time}`;
}

function orderCustomerName(order) {
	return [order.buyerName, order.buyerSurname].filter(Boolean).join(" ");
}

// A paid hard-copy order that hasn't been marked as dispatched yet.
function needsDispatch(order) {
	return (
		order.format === "hardCopy" &&
		(order.status || "pending") === "paid" &&
		(order.deliveryStatus || "new") !== "dispatched"
	);
}

function deliveryStatusAgeDays(order, now = Date.now()) {
	const lastStatusChange = order.deliveryUpdatedAt || order.paidAt || order.createdAt;
	const changedAt = lastStatusChange?.toDate
		? lastStatusChange.toDate().getTime()
		: lastStatusChange instanceof Date
			? lastStatusChange.getTime()
			: Number.NaN;
	if (!Number.isFinite(changedAt)) return null;
	return Math.max(0, Math.floor((now - changedAt) / (24 * 60 * 60 * 1000)));
}

function needsDeliveryFollowUp(order, now = Date.now()) {
	const statusAge = deliveryStatusAgeDays(order, now);
	return needsDispatch(order) && statusAge !== null && statusAge * 24 * 60 * 60 * 1000 >= BOOK_DELIVERY_FOLLOW_UP_MS;
}

const BookOrdersPanel = ({ db }) => {
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [formatFilter, setFormatFilter] = useState("all");
	const [dispatchOnly, setDispatchOnly] = useState(false);
	const [staleOnly, setStaleOnly] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const [copiedId, setCopiedId] = useState(null);
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		const unsub = onSnapshot(
			collection(db, "bookOrders"),
			(snap) => {
				const docs = snap.docs
					.map((d) => ({ id: d.id, ...d.data() }))
					.sort((a, b) => (b.createdAt?.seconds || b.createdAt?.getTime?.() / 1000 || 0) - (a.createdAt?.seconds || a.createdAt?.getTime?.() / 1000 || 0));
				setOrders(docs);
				setLoading(false);
			},
			(err) => { setError(err.message); setLoading(false); }
		);
		return unsub;
	}, [db]);

	useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), 60 * 1000);
		return () => window.clearInterval(timer);
	}, []);

	const setDeliveryStatus = async (order, deliveryStatus) => {
		setBusyId(order.id);
		try {
			await updateDoc(doc(db, "bookOrders", order.id), { deliveryStatus, deliveryUpdatedAt: serverTimestamp() });
			if (order.deliveryStatus !== deliveryStatus) {
				try {
					await sendBookDeliveryUpdate(order, deliveryStatus);
				} catch (emailError) {
					setError(`Delivery status was updated, but the buyer email could not be sent: ${emailError.message}`);
				}
			}
		} catch (err) {
			setError(err.message || "Could not update the delivery status.");
		} finally {
			setBusyId(null);
		}
	};

	const copyDetails = async (order) => {
		if (!navigator.clipboard) {
			setError("Copying isn't supported in this browser. Select the details and copy them manually.");
			return;
		}
		try {
			await navigator.clipboard.writeText(
				`${orderCustomerName(order)}\n${order.buyerPhone || ""}\n${order.buyerEmail || ""}\n${order.buyerAddress || ""}`
			);
			setCopiedId(order.id);
			setTimeout(() => setCopiedId((current) => (current === order.id ? null : current)), 1800);
		} catch (err) {
			setError("Couldn't copy to the clipboard. Please try again.");
		}
	};

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		return orders.filter((order) => {
			if (statusFilter !== "all" && (order.status || "pending") !== statusFilter) return false;
			if (formatFilter !== "all" && order.format !== formatFilter) return false;
			if (dispatchOnly && !needsDispatch(order)) return false;
			if (staleOnly && !needsDeliveryFollowUp(order, now)) return false;
			if (!term) return true;
			const haystack = [order.buyerName, order.buyerSurname, order.buyerEmail, order.bookTitle, order.orderId, order.buyerPhone]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return haystack.includes(term);
		});
	}, [orders, search, statusFilter, formatFilter, dispatchOnly, staleOnly, now]);

	// Headline numbers + per-status counts always reflect ALL orders, not the
	// current filter, so the chips read as "what's out there" at a glance.
	const summary = useMemo(() => {
		const counts = { all: orders.length, pending: 0, processing: 0, paid: 0, failed: 0 };
		let revenueCents = 0;
		let toDispatch = 0;
		let staleDeliveryUpdates = 0;
		orders.forEach((order) => {
			const status = order.status || "pending";
			if (counts[status] !== undefined) counts[status] += 1;
			if (status === "paid") revenueCents += Number(order.amountCents || 0);
			if (needsDispatch(order)) toDispatch += 1;
			if (needsDeliveryFollowUp(order, now)) staleDeliveryUpdates += 1;
		});
		const currency = orders.find((order) => order.currency)?.currency;
		return {
			counts,
			toDispatch,
			staleDeliveryUpdates,
			awaiting: counts.pending + counts.processing,
			revenue: orderMoney({ amountCents: revenueCents, currency }),
		};
	}, [orders, now]);

	const visible = filtered.slice(0, 200);
	const hasFilters = Boolean(search.trim()) || statusFilter !== "all" || formatFilter !== "all" || dispatchOnly || staleOnly;
	const clearFilters = () => {
		setSearch("");
		setStatusFilter("all");
		setFormatFilter("all");
		setDispatchOnly(false);
		setStaleOnly(false);
	};

	return (
		<div className="adm-books-orders">
			{error && <div className="adm-query-error" role="alert">{error}</div>}

			<div className="adm-orders__summary">
				<div className="adm-orders__stat">
					<span className="adm-orders__stat-icon adm-orders__stat-icon--green"><TrendingUp size={18} strokeWidth={2.2} /></span>
					<div>
						<div className="adm-orders__stat-label">Paid revenue</div>
						<div className="adm-orders__stat-value">{summary.revenue}</div>
					</div>
				</div>
				<div className="adm-orders__stat">
					<span className="adm-orders__stat-icon"><Check size={18} strokeWidth={2.4} /></span>
					<div>
						<div className="adm-orders__stat-label">Paid orders</div>
						<div className="adm-orders__stat-value">{summary.counts.paid}</div>
					</div>
				</div>
				<div className="adm-orders__stat">
					<span className="adm-orders__stat-icon adm-orders__stat-icon--amber"><Clock size={18} strokeWidth={2.2} /></span>
					<div>
						<div className="adm-orders__stat-label">Awaiting payment</div>
						<div className="adm-orders__stat-value">{summary.awaiting}</div>
					</div>
				</div>
				<div className="adm-orders__stat">
					<span className="adm-orders__stat-icon adm-orders__stat-icon--purple"><Truck size={18} strokeWidth={2.2} /></span>
					<div>
						<div className="adm-orders__stat-label">To dispatch</div>
						<div className="adm-orders__stat-value">{summary.toDispatch}</div>
					</div>
				</div>
			</div>

			<div className="adm-orders__toolbar">
				<label className="adm-orders__search">
					<Search size={16} strokeWidth={2.2} aria-hidden="true" />
					<input
						type="search"
						placeholder="Search buyer, email, phone or book"
						aria-label="Search orders"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</label>

				<div className="adm-orders__filters">
					<div className="adm-orders__chips" role="group" aria-label="Filter by payment status">
						{["all", "paid", "pending", "processing", "failed"].map((s) => (
							<button key={s} type="button" aria-pressed={statusFilter === s} className={statusFilter === s ? "is-active" : ""} onClick={() => setStatusFilter(s)}>
								{s === "all" ? "All" : ORDER_STATUS_LABELS[s]}
								<em>{summary.counts[s]}</em>
							</button>
						))}
					</div>

					<div className="adm-orders__chips" role="group" aria-label="Filter by format">
						{["all", "pdf", "hardCopy"].map((f) => (
							<button key={f} type="button" aria-pressed={formatFilter === f} className={formatFilter === f ? "is-active" : ""} onClick={() => setFormatFilter(f)}>
								{f === "all" ? "All formats" : f === "pdf" ? "PDF" : "Hard copy"}
							</button>
						))}
					</div>

					<button type="button" aria-pressed={dispatchOnly} className={`adm-orders__toggle${dispatchOnly ? " is-active" : ""}`} onClick={() => setDispatchOnly((v) => !v)}>
						<Truck size={14} strokeWidth={2.2} />
						Needs dispatch
						<em>{summary.toDispatch}</em>
					</button>
					<button type="button" aria-pressed={staleOnly} className={`adm-orders__toggle${staleOnly ? " is-active" : ""}`} onClick={() => setStaleOnly((v) => !v)}>
						<Clock size={14} strokeWidth={2.2} />
						No update for 2+ days
						<em>{summary.staleDeliveryUpdates}</em>
					</button>
				</div>
			</div>
			{summary.staleDeliveryUpdates > 0 && (
				<div className="adm-query-error adm-orders__stale-alert" role="status">
					<Clock size={16} />
					<strong>{summary.staleDeliveryUpdates} paid hard-copy order{summary.staleDeliveryUpdates === 1 ? " has" : "s have"} had no delivery-status update for at least 2 days.</strong>
					<span>These will also be included in the enabled daily admin email report.</span>
				</div>
			)}

			{loading ? (
				<div className="adm-delivery-grid" aria-busy="true">
					<span className="adm-orders__sr" role="status">Loading orders…</span>
					{[0, 1, 2, 3, 4, 5].map((i) => (
						<div className="adm-delivery-card adm-order-skeleton" key={i} aria-hidden="true">
							<span /><span /><span /><span />
						</div>
					))}
				</div>
			) : visible.length === 0 ? (
				<div className="adm-orders__empty">
					<span className="adm-orders__empty-icon"><Inbox size={22} strokeWidth={2} /></span>
					<strong>{orders.length === 0 ? "No orders yet" : "No orders match these filters"}</strong>
					<p>{orders.length === 0 ? "Book purchases show up here as soon as a customer checks out." : "Try a different search, or clear the filters to see every order."}</p>
					{hasFilters && <button type="button" className="adm-ghost-btn" onClick={clearFilters}>Clear filters</button>}
				</div>
			) : (
				<>
					<div className="adm-orders__count" aria-live="polite">
						{hasFilters ? `${filtered.length} of ${orders.length} orders` : `${orders.length} order${orders.length === 1 ? "" : "s"}`}
					</div>
					<div className="adm-delivery-grid">
						{visible.map((order) => {
							const status = order.status || "pending";
							const isHardCopy = order.format === "hardCopy";
							const deliveryStatus = order.deliveryStatus || "new";
							const staleDelivery = needsDeliveryFollowUp(order, now);
							const staleDays = deliveryStatusAgeDays(order, now);
							const customer = orderCustomerName(order);
							const isBusy = busyId === order.id;
							return (
								<article className="adm-delivery-card" key={order.id}>
									<div className="adm-delivery-card__top">
										<span className={`adm-delivery-status is-${status}`}>{ORDER_STATUS_LABELS[status] || status}</span>
										<strong>{orderMoney(order)}</strong>
									</div>

									<div className="adm-order-card__title">
										<h3>{order.bookTitle || "Book order"}</h3>
										<div className="adm-order-card__meta">
											<span className={`adm-format-chip${isHardCopy ? " is-physical" : ""}`}>
												{isHardCopy ? <Package size={13} strokeWidth={2.2} /> : <FileText size={13} strokeWidth={2.2} />}
												{isHardCopy ? "Hard copy" : "PDF"}
											</span>
											<span>{orderWhen(order.createdAt)}</span>
										</div>
										{staleDelivery && (
											<p className="adm-order-card__stale">
												<Clock size={13} /> No delivery-status change for {staleDays} days
											</p>
										)}
									</div>

									<div className="adm-order-card__customer">
										<div className={`adm-customer-avatar adm-customer-avatar--${avatarColorFor(customer)}`}>
											{initialsFor(customer) || "?"}
										</div>
										<div className="adm-order-card__contact">
											<strong>{customer || "Unnamed customer"}</strong>
											{order.buyerEmail && (
												<a href={`mailto:${order.buyerEmail}`}><Mail size={13} strokeWidth={2.2} />{order.buyerEmail}</a>
											)}
											{isHardCopy && order.buyerPhone && (
												<a href={`tel:${String(order.buyerPhone).replace(/\s/g, "")}`}><Phone size={13} strokeWidth={2.2} />{order.buyerPhone}</a>
											)}
										</div>
									</div>

									{isHardCopy && (
										<div className="adm-delivery-card__address">
											<span><MapPin size={13} strokeWidth={2.2} />Delivery address</span>
											<p className={order.buyerAddress ? "" : "is-empty"}>{order.buyerAddress || "No address captured"}</p>
										</div>
									)}

									{isHardCopy && status === "paid" && (
										<div className="adm-delivery-card__actions">
											<button className="adm-ghost-btn adm-btn-sm" type="button" onClick={() => copyDetails(order)}>
												{copiedId === order.id ? <><Check size={14} strokeWidth={2.4} /> Copied</> : <><Copy size={14} strokeWidth={2.2} /> Copy details</>}
											</button>
											{deliveryStatus === "dispatched" ? (
												<span className="adm-delivery-status is-dispatched has-icon"><Check size={13} strokeWidth={2.6} />Dispatched</span>
											) : (
												<button className="adm-primary-btn adm-btn-sm" type="button" disabled={isBusy} onClick={() => setDeliveryStatus(order, "dispatched")}>
													<Truck size={14} strokeWidth={2.2} />
													{isBusy ? "Saving…" : "Mark dispatched"}
												</button>
											)}
										</div>
									)}
								</article>
							);
						})}
					</div>
				</>
			)}

			{filtered.length > visible.length && (
				<div className="adm-panel__heading-sub" style={{ marginTop: 4 }}>
					Showing the {visible.length} most recent of {filtered.length} matching orders. Narrow the search or filters to see the rest.
				</div>
			)}
		</div>
	);
};

// ── Keynotes Tab ──────────────────────────────────────────────────────────────
// Manages the "keynotes" Firestore collection consumed by the public
// Keynotes page (src/pages/Keynotes.jsx).
// Doc shape: { title, eyebrow, description, steps: [], photoUrl, photoPath,
//              synopsisUrl, synopsisPath, synopsisFileName, videoUrl,
//              videoLabel, available, order }

const KeynotesTab = ({ db }) => {
	const [keynotes, setKeynotes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [photoProgress, setPhotoProgress] = useState(0);
	const [pdfProgress, setPdfProgress] = useState(0);
	const [error, setError] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editItem, setEditItem] = useState(null);

	const [form, setForm] = useState({
		title: "", eyebrow: "", description: "", stepsText: "",
		videoUrl: "", videoLabel: "", available: true,
	});

	const [photoFile, setPhotoFile] = useState(null);
	const [photoPreview, setPhotoPreview] = useState(null);
	const photoRef = useRef();

	const [synopsisFile, setSynopsisFile] = useState(null);
	const [synopsisName, setSynopsisName] = useState(null);
	const synopsisRef = useRef();

	useEffect(() => {
		const unsub = onSnapshot(collection(db, "keynotes"), (snap) => {
			const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			docs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
			setKeynotes(docs);
			setLoading(false);
		}, (err) => { setError(err.message); setLoading(false); });
		return unsub;
	}, [db]);

	const resetForm = () => {
		setForm({ title: "", eyebrow: "", description: "", stepsText: "", videoUrl: "", videoLabel: "", available: true });
		setPhotoFile(null); setPhotoPreview(null);
		setSynopsisFile(null); setSynopsisName(null);
		setEditItem(null); setShowForm(false);
	};

	const openEdit = (item) => {
		setEditItem(item);
		setForm({
			title: item.title || "",
			eyebrow: item.eyebrow || "",
			description: item.description || "",
			stepsText: (item.steps || []).join(", "),
			videoUrl: item.videoUrl || "",
			videoLabel: item.videoLabel || "",
			available: item.available !== false,
		});
		setPhotoPreview(item.photoUrl || null);
		setPhotoFile(null);
		setSynopsisName(item.synopsisFileName || (item.synopsisUrl ? "Existing synopsis" : null));
		setSynopsisFile(null);
		setShowForm(true);
	};

	const handlePhotoChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setPhotoFile(file);
		setPhotoPreview(URL.createObjectURL(file));
	};

	const handleSynopsisChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		if (file.type !== "application/pdf") {
			setError("Please choose a PDF file for the synopsis.");
			return;
		}
		setError(null);
		setSynopsisFile(file);
		setSynopsisName(file.name);
	};

	const handleSubmit = async () => {
		if (!form.title.trim()) { setError("Title is required."); return; }
		setError(null);
		setSaving(true);
		setPhotoProgress(0);
		setPdfProgress(0);

		try {
			let photoUrl = editItem?.photoUrl || "";
			let photoPath = editItem?.photoPath || "";
			let synopsisUrl = editItem?.synopsisUrl || "";
			let synopsisPath = editItem?.synopsisPath || "";
			let synopsisFileName = editItem?.synopsisFileName || "";

			if (photoFile) {
				const result = await supabaseUploadKeynotePhoto(photoFile, setPhotoProgress);
				photoUrl = result.url;
				photoPath = result.path;
				if (editItem?.photoPath) await supabaseDeleteKeynotePhoto(editItem.photoPath);
			}

			if (synopsisFile) {
				const result = await supabaseUploadKeynoteSynopsis(synopsisFile, setPdfProgress);
				synopsisUrl = result.url;
				synopsisPath = result.path;
				synopsisFileName = synopsisFile.name;
			}

			const steps = form.stepsText
				.split(",")
				.map(s => s.trim())
				.filter(Boolean);

			const payload = {
				title: form.title.trim(),
				eyebrow: form.eyebrow.trim(),
				description: form.description.trim(),
				steps,
				photoUrl,
				photoPath,
				synopsisUrl,
				synopsisPath,
				synopsisFileName,
				videoUrl: form.videoUrl.trim(),
				videoLabel: form.videoLabel.trim(),
				available: form.available,
				updatedAt: serverTimestamp(),
			};

			if (editItem) {
				await updateDoc(doc(db, "keynotes", editItem.id), payload);
			} else {
				await addDoc(collection(db, "keynotes"), {
					...payload,
					order: keynotes.length,
					createdAt: serverTimestamp(),
				});
			}
			resetForm();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
			setPhotoProgress(0);
			setPdfProgress(0);
		}
	};

	const handleDelete = async (item) => {
		if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
		try {
			await deleteDoc(doc(db, "keynotes", item.id));
			if (item.photoPath) await supabaseDeleteKeynotePhoto(item.photoPath);
			if (item.synopsisPath) await supabaseDeleteKeynoteSynopsis(item.synopsisPath);
		} catch (err) { setError(err.message); }
	};

	// Same reorder pattern as the Media tab — swaps `order` so admins can
	// control the sequence shown on the public page.
	const moveItem = async (index, direction) => {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= keynotes.length) return;
		const a = keynotes[index];
		const b = keynotes[targetIndex];
		try {
			await updateDoc(doc(db, "keynotes", a.id), { order: b.order ?? targetIndex });
			await updateDoc(doc(db, "keynotes", b.id), { order: a.order ?? index });
		} catch (err) { setError(err.message); }
	};

	return (
		<div className="adm-books-tab">
			{error && <div className="adm-query-error">{error}</div>}

			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">Keynotes</div>
					<div className="adm-books-sub">{keynotes.length} talk{keynotes.length !== 1 ? "s" : ""} listed</div>
				</div>
				<button className="adm-primary-btn" onClick={() => { resetForm(); setShowForm(true); }}>
					+ Add keynote
				</button>
			</div>

			{/* ── Add / Edit Form ── */}
			{showForm && (
				<div className="adm-book-form">
					<div className="adm-book-form__title">{editItem ? "Edit keynote" : "Add new keynote"}</div>

					<div className="adm-book-form__grid">
						{/* Photo upload */}
						<div
							className="adm-cover-upload"
							onClick={() => photoRef.current?.click()}
							style={photoPreview ? { backgroundImage: `url(${photoPreview})` } : {}}
						>
							{!photoPreview && (
								<div className="adm-cover-upload__placeholder">
									<span className="adm-cover-upload__icon">🎤</span>
									<span>Upload photo</span>
								</div>
							)}
							<input
								ref={photoRef}
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={handlePhotoChange}
							/>
						</div>

						{/* Fields */}
						<div className="adm-book-form__fields">
							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Title *</label>
									<input
										className="adm-field-input"
										value={form.title}
										onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
										placeholder="e.g. The 7Cs of High Performance"
									/>
								</div>
								<div>
									<label className="adm-field-label">Eyebrow / tag</label>
									<input
										className="adm-field-input"
										value={form.eyebrow}
										onChange={e => setForm(f => ({ ...f, eyebrow: e.target.value }))}
										placeholder="e.g. Signature keynote"
									/>
								</div>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Description</label>
								<textarea
									className="adm-field-input adm-field-textarea"
									value={form.description}
									onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
									placeholder="What this talk is about and who it's for"
									rows={4}
								/>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Steps (comma-separated, optional)</label>
								<input
									className="adm-field-input"
									value={form.stepsText}
									onChange={e => setForm(f => ({ ...f, stepsText: e.target.value }))}
									placeholder="e.g. Wake Up, Rise Up, Armour Up, Keep Up, Team Up"
								/>
							</div>

							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Video URL</label>
									<input
										className="adm-field-input"
										value={form.videoUrl}
										onChange={e => setForm(f => ({ ...f, videoUrl: e.target.value }))}
										placeholder="https://..."
									/>
								</div>
								<div>
									<label className="adm-field-label">Video button label</label>
									<input
										className="adm-field-input"
										value={form.videoLabel}
										onChange={e => setForm(f => ({ ...f, videoLabel: e.target.value }))}
										placeholder="e.g. Watch highlights"
									/>
								</div>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Available?</label>
								<div className="adm-toggle-row">
									<label className="adm-toggle">
										<input
											type="checkbox"
											checked={form.available}
											onChange={e => setForm(f => ({ ...f, available: e.target.checked }))}
										/>
										<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
									</label>
									<span className="adm-toggle-label">{form.available ? "Yes" : "No"}</span>
								</div>
							</div>

							{/* Synopsis PDF upload */}
							<div className="adm-field-row">
								<label className="adm-field-label">Synopsis PDF</label>
								<div className="adm-pdf-upload" onClick={() => synopsisRef.current?.click()}>
									<span className="adm-pdf-upload__icon">📄</span>
									<span className="adm-pdf-upload__text">
										{synopsisName ? synopsisName : "Click to choose a PDF file"}
									</span>
									<span className="adm-pdf-upload__action">
										{synopsisName ? "Replace" : "Browse"}
									</span>
								</div>
								<input
									ref={synopsisRef}
									type="file"
									accept="application/pdf"
									style={{ display: "none" }}
									onChange={handleSynopsisChange}
								/>
							</div>
						</div>
					</div>

					{saving && (
						<>
							{photoFile && (
								<div className="adm-upload-progress">
									<div className="adm-upload-progress__bar" style={{ width: `${photoProgress}%` }} />
									<span>Photo {photoProgress}%</span>
								</div>
							)}
							{synopsisFile && (
								<div className="adm-upload-progress">
									<div className="adm-upload-progress__bar" style={{ width: `${pdfProgress}%` }} />
									<span>Synopsis {pdfProgress}%</span>
								</div>
							)}
						</>
					)}

					<div className="adm-book-form__actions">
						<button className="adm-ghost-btn" onClick={resetForm} disabled={saving}>Cancel</button>
						<button className="adm-primary-btn" onClick={handleSubmit} disabled={saving}>
							{saving ? "Saving…" : editItem ? "Save changes" : "Publish keynote"}
						</button>
					</div>
				</div>
			)}

			{/* ── Keynote list ── */}
			{loading ? (
				<div className="adm-bookings-empty">Loading keynotes…</div>
			) : keynotes.length === 0 ? (
				<div className="adm-bookings-empty">
					<div className="adm-bookings-empty__icon">🎤</div>
					<div>No keynotes yet. Add your first talk above.</div>
				</div>
			) : (
				<div className="adm-book-list">
					{keynotes.map((item, i) => (
						<div key={item.id} className={`adm-book-card${!item.available ? " adm-book-card--unavailable" : ""}`}>
							{item.photoUrl
								? <img className="adm-book-card__cover" src={item.photoUrl} alt={item.title} />
								: <div className="adm-book-card__cover adm-book-card__cover--placeholder">🎤</div>
							}
							<div className="adm-book-card__body">
								<div className="adm-book-card__title">{item.title}</div>
								{item.eyebrow && <div className="adm-book-card__author">{item.eyebrow}</div>}
								{item.description && <div className="adm-book-card__desc">{item.description}</div>}
								{item.steps?.length > 0 && (
									<div className="adm-book-card__desc" style={{ fontStyle: "italic" }}>
										{item.steps.join(" → ")}
									</div>
								)}
								<div className="adm-book-card__footer" style={{ flexWrap: "wrap", gap: 6 }}>
									{!item.available && <span className="adm-badge adm-badge--muted">Unlisted</span>}
									{item.synopsisUrl
										? <span className="adm-badge adm-badge--blue">Synopsis attached</span>
										: <span className="adm-badge adm-badge--muted">No synopsis</span>}
									{item.videoUrl && <span className="adm-badge adm-badge--blue">Video linked</span>}
								</div>
							</div>
							<div className="adm-book-card__actions">
								<button className="adm-icon-btn" title="Move up" onClick={() => moveItem(i, -1)} disabled={i === 0}>↑</button>
								<button className="adm-icon-btn" title="Move down" onClick={() => moveItem(i, 1)} disabled={i === keynotes.length - 1}>↓</button>
								<button className="adm-icon-btn" title="Edit" onClick={() => openEdit(item)}>✏️</button>
								<button className="adm-icon-btn adm-icon-btn--danger" title="Delete" onClick={() => handleDelete(item)}>🗑</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

// ── Media Tab ─────────────────────────────────────────────────────────────────
// Manages the "media" Firestore collection consumed by the public Media page
// (src/pages/Projects.jsx). Each doc: { type, platform, title, url, cta, order }

const MediaTab = ({ db }) => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editItem, setEditItem] = useState(null);

	const [form, setForm] = useState({ type: "video", platform: "", title: "", url: "", cta: "" });

	useEffect(() => {
		const unsub = onSnapshot(collection(db, "media"), (snap) => {
			const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			docs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
			setItems(docs);
			setLoading(false);
		}, (err) => { setError(err.message); setLoading(false); });
		return unsub;
	}, [db]);

	const resetForm = () => {
		setForm({ type: "video", platform: "", title: "", url: "", cta: "" });
		setEditItem(null); setShowForm(false);
	};

	const openEdit = (item) => {
		setEditItem(item);
		setForm({
			type: item.type || "video",
			platform: item.platform || "",
			title: item.title || "",
			url: item.url || "",
			cta: item.cta || "",
		});
		setShowForm(true);
	};

	const handleSubmit = async () => {
		if (!form.platform.trim() || !form.title.trim() || !form.url.trim()) {
			setError("Platform, title, and URL are required.");
			return;
		}
		try {
			// eslint-disable-next-line no-new
			new URL(form.url); // throws if not a well-formed absolute URL
		} catch {
			setError("Please enter a valid URL (including https://).");
			return;
		}

		setError(null);
		setSaving(true);
		try {
			const payload = {
				type: form.type,
				platform: form.platform.trim(),
				title: form.title.trim(),
				url: form.url.trim(),
				cta: form.cta.trim() || defaultCta(form.type),
				updatedAt: serverTimestamp(),
			};
			if (editItem) {
				await updateDoc(doc(db, "media", editItem.id), payload);
			} else {
				await addDoc(collection(db, "media"), {
					...payload,
					order: items.length,
					createdAt: serverTimestamp(),
				});
			}
			resetForm();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (item) => {
		if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
		try {
			await deleteDoc(doc(db, "media", item.id));
		} catch (err) { setError(err.message); }
	};

	// Swaps `order` values so admins can reorder how items appear on the
	// public site. Two sequential updateDoc calls — fine for a single-admin
	// dashboard; wrap in a writeBatch if concurrent edits ever become a concern.
	const moveItem = async (index, direction) => {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= items.length) return;
		const a = items[index];
		const b = items[targetIndex];
		try {
			await updateDoc(doc(db, "media", a.id), { order: b.order ?? targetIndex });
			await updateDoc(doc(db, "media", b.id), { order: a.order ?? index });
		} catch (err) { setError(err.message); }
	};

	const previewThumb = form.type === "video" ? getYouTubeId(form.url) : null;

	return (
		<div className="adm-books-tab">
			{error && <div className="adm-query-error">{error}</div>}

			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">Media appearances</div>
					<div className="adm-books-sub">{items.length} item{items.length !== 1 ? "s" : ""} listed</div>
				</div>
				<button className="adm-primary-btn" onClick={() => { resetForm(); setShowForm(true); }}>
					+ Add media item
				</button>
			</div>

			{/* ── Add / Edit Form ── */}
			{showForm && (
				<div className="adm-book-form">
					<div className="adm-book-form__title">{editItem ? "Edit media item" : "Add media item"}</div>

					<div className="adm-field-row adm-field-row--half">
						<div>
							<label className="adm-field-label">Type *</label>
							<select
								className="adm-field-input"
								value={form.type}
								onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
							>
								{MEDIA_TYPE_OPTIONS.map(opt => (
									<option key={opt.id} value={opt.id}>{opt.label}</option>
								))}
							</select>
						</div>
						<div>
							<label className="adm-field-label">Platform *</label>
							<input
								className="adm-field-input"
								value={form.platform}
								onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
								placeholder="e.g. Talk Radio 702"
							/>
						</div>
					</div>

					<div className="adm-field-row">
						<label className="adm-field-label">Title *</label>
						<input
							className="adm-field-input"
							value={form.title}
							onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
							placeholder="Segment or article title"
						/>
					</div>

					<div className="adm-field-row">
						<label className="adm-field-label">URL *</label>
						<input
							className="adm-field-input"
							value={form.url}
							onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
							placeholder="https://..."
						/>
					</div>

					{previewThumb && (
						<div className="adm-field-row">
							<label className="adm-field-label">Preview</label>
							<img
								src={`https://img.youtube.com/vi/${previewThumb}/hqdefault.jpg`}
								alt="YouTube thumbnail preview"
								style={{ width: 200, borderRadius: 8, display: "block" }}
							/>
						</div>
					)}

					<div className="adm-field-row">
						<label className="adm-field-label">CTA label</label>
						<input
							className="adm-field-input"
							value={form.cta}
							onChange={e => setForm(f => ({ ...f, cta: e.target.value }))}
							placeholder={defaultCta(form.type)}
						/>
					</div>

					<div className="adm-book-form__actions">
						<button className="adm-ghost-btn" onClick={resetForm} disabled={saving}>Cancel</button>
						<button className="adm-primary-btn" onClick={handleSubmit} disabled={saving}>
							{saving ? "Saving…" : editItem ? "Save changes" : "Publish item"}
						</button>
					</div>
				</div>
			)}

			{/* ── Item list ── */}
			{loading ? (
				<div className="adm-bookings-empty">Loading media…</div>
			) : items.length === 0 ? (
				<div className="adm-bookings-empty">
					<div className="adm-bookings-empty__icon">🗞️</div>
					<div>No media items yet. Add your first appearance above.</div>
				</div>
			) : (
				<div className="adm-book-list">
					{items.map((item, i) => (
						<div key={item.id} className="adm-book-card">
							{item.type === "video" && getYouTubeId(item.url)
								? (
									<img
										className="adm-book-card__cover"
										src={`https://img.youtube.com/vi/${getYouTubeId(item.url)}/hqdefault.jpg`}
										alt={item.title}
									/>
								)
								: <div className="adm-book-card__cover adm-book-card__cover--placeholder">
									{item.type === "radio" ? "◎" : "✦"}
								</div>
							}
							<div className="adm-book-card__body">
								<div className="adm-book-card__title">{item.title}</div>
								<div className="adm-book-card__author">{item.platform} · {item.type}</div>
								<div className="adm-book-card__desc">{item.url}</div>
								<div className="adm-book-card__footer">
									<span className="adm-badge adm-badge--blue">{item.cta}</span>
								</div>
							</div>
							<div className="adm-book-card__actions">
								<button className="adm-icon-btn" title="Move up" onClick={() => moveItem(i, -1)} disabled={i === 0}>↑</button>
								<button className="adm-icon-btn" title="Move down" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1}>↓</button>
								<button className="adm-icon-btn" title="Edit" onClick={() => openEdit(item)}>✏️</button>
								<button className="adm-icon-btn adm-icon-btn--danger" title="Delete" onClick={() => handleDelete(item)}>🗑</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};


// Event tab ------------------------------

// ── Events Tab ────────────────────────────────────────────────────────────────
// Manages the "events" Firestore collection.
// Doc shape: { title, description, date, time, location, coverUrl, coverPublicId,
//              basePrice, currency,
//              ticketTiers: [{ id, name, price, quantity, sold, reserved }],
//              available, order }
// quantity: admin-set limit for that tier — 0 (or blank) means unlimited.
// sold: incremented server-side (Vercel webhook) once a Yoco payment succeeds.
// reserved: incremented server-side while a checkout is pending, so two buyers
// can't both grab the last ticket while one is mid-payment. Both fields are
// managed by the checkout functions, not edited here directly.

const makeTierId = () => Math.random().toString(36).slice(2, 9);

function isFreeTicketPrice(value) {
	const text = String(value ?? "").trim();
	if (!text) return false;
	const match = text.match(/(?:R\s*)?([\d,]+(?:\.\d+)?)/i);
	return Boolean(match) && Number(match[1].replace(/,/g, "")) === 0;
}

const EventsTab = ({ db }) => {
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [uploadProgress, setProgress] = useState(0);
	const [error, setError] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editEvent, setEditEvent] = useState(null);

	const [form, setForm] = useState({
		title: "", description: "", date: "", time: "", location: "",
		basePrice: "", currency: "ZAR", available: true,
	});
	const [tiers, setTiers] = useState([{ id: makeTierId(), name: "", price: "", quantity: "" }]);

	const [coverFile, setCoverFile] = useState(null);
	const [coverPreview, setCoverPreview] = useState(null);
	const fileRef = useRef();

	useEffect(() => {
		const unsub = onSnapshot(collection(db, "events"), (snap) => {
			setEvents(
				snap.docs.map(d => ({ id: d.id, ...d.data() }))
					.sort((a, b) => (a.date || "").localeCompare(b.date || ""))
			);
			setLoading(false);
		}, (err) => { setError(err.message); setLoading(false); });
		return unsub;
	}, [db]);

	const resetForm = () => {
		setForm({ title: "", description: "", date: "", time: "", location: "", basePrice: "", currency: "ZAR", available: true });
		setTiers([{ id: makeTierId(), name: "", price: "", quantity: "" }]);
		setCoverFile(null); setCoverPreview(null);
		setEditEvent(null); setShowForm(false);
	};

	const openEdit = (ev) => {
		setEditEvent(ev);
		setForm({
			title: ev.title || "", description: ev.description || "",
			date: ev.date || "", time: ev.time || "", location: ev.location || "",
			basePrice: ev.basePrice || "", currency: ev.currency || "ZAR",
			available: ev.available !== false,
		});
		setTiers(
			ev.ticketTiers?.length
				? ev.ticketTiers.map(t => ({
					...t,
					id: t.id || makeTierId(),
					// 0/undefined means "unlimited" — show the field blank rather than 0
					// so the admin isn't confused into thinking it's sold out.
					quantity: t.quantity ? String(t.quantity) : "",
				}))
				: [{ id: makeTierId(), name: "", price: "", quantity: "" }]
		);
		setCoverPreview(ev.coverUrl || null);
		setCoverFile(null);
		setShowForm(true);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setCoverFile(file);
		setCoverPreview(URL.createObjectURL(file));
	};

	// ── Ticket tier helpers ──
	const addTier = () => setTiers(t => [...t, { id: makeTierId(), name: "", price: "", quantity: "" }]);
	const removeTier = (id) => setTiers(t => t.length > 1 ? t.filter(x => x.id !== id) : t);
	const updateTier = (id, field, value) =>
		setTiers(t => t.map(x => x.id === id ? { ...x, [field]: value } : x));

	const handleSubmit = async () => {
		if (!form.title.trim() || !form.date) { setError("Title and date are required."); return; }

		setError(null);
		setSaving(true);
		setProgress(0);

		try {
			const cleanTiers = tiers
				.filter(t => t.name.trim())
				.map(t => ({
					id: t.id,
					name: t.name.trim(),
					price: isFreeTicketPrice(t.price)
						? 0
						: parseAdminAmount(t.price, `${t.name.trim()} price`, { required: true }),
					quantity: parseInt(t.quantity, 10) || 0, // 0 = unlimited
					sold: t.sold || 0,
					reserved: t.reserved || 0,
				}));
			const basePrice = parseAdminAmount(form.basePrice, "Base ticket price");
			let coverUrl = editEvent?.coverUrl || "";
			let coverPublicId = editEvent?.coverPublicId || "";

			if (coverFile) {
				const result = await cloudinaryUpload(coverFile, setProgress);
				coverUrl = result.url;
				coverPublicId = result.publicId;
				if (editEvent?.coverPublicId) await cloudinaryDelete(editEvent.coverPublicId);
			}

			const payload = {
				title: form.title.trim(),
				description: form.description.trim(),
				date: form.date,
				time: form.time,
				location: form.location.trim(),
				basePrice,
				currency: form.currency,
				available: form.available,
				coverUrl,
				coverPublicId,
				ticketTiers: cleanTiers,
				updatedAt: serverTimestamp(),
			};

			if (editEvent) {
				await updateDoc(doc(db, "events", editEvent.id), payload);
			} else {
				await addDoc(collection(db, "events"), { ...payload, createdAt: serverTimestamp() });
			}
			resetForm();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
			setProgress(0);
		}
	};

	const handleDelete = async (ev) => {
		if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
		try {
			await deleteDoc(doc(db, "events", ev.id));
			if (ev.coverPublicId) await cloudinaryDelete(ev.coverPublicId);
		} catch (err) { setError(err.message); }
	};

	// Remaining stock for a tier — null means unlimited (no quantity cap set).
	const remainingFor = (tier) => {
		const limit = tier.quantity || 0;
		if (limit <= 0) return null;
		const sold = tier.sold || 0;
		const reserved = tier.reserved || 0;
		return Math.max(0, limit - sold - reserved);
	};

	return (
		<div className="adm-books-tab">
			{error && <div className="adm-query-error">{error}</div>}

			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">Events</div>
					<div className="adm-books-sub">{events.length} event{events.length !== 1 ? "s" : ""} listed</div>
				</div>
				<button className="adm-primary-btn" onClick={() => { resetForm(); setShowForm(true); }}>
					+ Add event
				</button>
			</div>

			{showForm && (
				<div className="adm-book-form">
					<div className="adm-book-form__title">{editEvent ? "Edit event" : "Add new event"}</div>

					<div className="adm-book-form__grid">
						{/* Cover / picture upload */}
						<div
							className="adm-cover-upload"
							onClick={() => fileRef.current?.click()}
							style={coverPreview ? { backgroundImage: `url(${coverPreview})` } : {}}
						>
							{!coverPreview && (
								<div className="adm-cover-upload__placeholder">
									<span className="adm-cover-upload__icon">📷</span>
									<span>Upload event picture</span>
								</div>
							)}
							<input
								ref={fileRef}
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={handleFileChange}
							/>
						</div>

						<div className="adm-book-form__fields">
							<div className="adm-field-row">
								<label className="adm-field-label">Title *</label>
								<input
									className="adm-field-input"
									value={form.title}
									onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
									placeholder="Event title"
								/>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Description</label>
								<textarea
									className="adm-field-input adm-field-textarea"
									value={form.description}
									onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
									placeholder="Event details shown to attendees"
									rows={3}
								/>
							</div>

							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Date *</label>
									<input
										className="adm-field-input"
										type="date"
										value={form.date}
										onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
									/>
								</div>
								<div>
									<label className="adm-field-label">Time</label>
									<input
										className="adm-field-input"
										type="time"
										value={form.time}
										onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
									/>
								</div>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Location</label>
								<input
									className="adm-field-input"
									value={form.location}
									onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
									placeholder="Venue / address"
								/>
							</div>

							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Base ticket price (ZAR) — R 850 format</label>
									<input
										className="adm-field-input"
										type="text"
										inputMode="decimal"
										min="0"
										step="1"
										value={form.basePrice}
										onChange={e => setForm(f => ({ ...f, basePrice: e.target.value }))}
										placeholder="R 150"
									/>
								</div>
								<div>
									<label className="adm-field-label">Available?</label>
									<div className="adm-toggle-row">
										<label className="adm-toggle">
											<input
												type="checkbox"
												checked={form.available}
												onChange={e => setForm(f => ({ ...f, available: e.target.checked }))}
											/>
											<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
										</label>
										<span className="adm-toggle-label">{form.available ? "Yes" : "No"}</span>
									</div>
								</div>
							</div>

							{/* Ticket tiers */}
							<div className="adm-field-row">
								<label className="adm-field-label">Ticket standards (e.g. VIP, VVIP, General)</label>
								<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
									{tiers.map((tier) => {
										const remaining = remainingFor(tier);
										const isFree = isFreeTicketPrice(tier.price);
										return (
											<div key={tier.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
												<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
													<input
														className="adm-field-input"
														style={{ flex: 2 }}
														value={tier.name}
														onChange={e => updateTier(tier.id, "name", e.target.value)}
														placeholder="Tier name, e.g. VIP"
													/>
													<input
														className="adm-field-input"
														style={{ flex: 1 }}
														type="text"
														inputMode="decimal"
														min="0"
														step="1"
														value={tier.price}
														onChange={e => updateTier(tier.id, "price", e.target.value)}
														placeholder="R 150 or 0 for free"
														disabled={isFree}
														aria-label={`${tier.name || "Ticket"} price`}
													/>
													<input
														className="adm-field-input"
														style={{ flex: 1 }}
														type="number"
														min="0"
														step="1"
														value={tier.quantity}
														onChange={e => updateTier(tier.id, "quantity", e.target.value)}
														placeholder="Limit (blank = ∞)"
													/>
													<button
														type="button"
														className="adm-icon-btn adm-icon-btn--danger"
														title="Remove tier"
														onClick={() => removeTier(tier.id)}
														disabled={tiers.length === 1}
													>
														🗑
													</button>
												</div>
												<label className="adm-ticket-free-toggle">
													<input
														type="checkbox"
														checked={isFree}
														onChange={e => updateTier(tier.id, "price", e.target.checked ? "0" : "")}
													/>
													<span>Free ticket</span>
													{isFree && <span className="adm-ticket-free-toggle__note">Customers skip Yoco checkout</span>}
												</label>
												{(tier.sold > 0 || tier.reserved > 0) && (
													<div style={{ fontSize: 11.5, color: "var(--adm-ink-faint)", paddingLeft: 4 }}>
														{tier.sold || 0} sold
														{tier.reserved ? ` · ${tier.reserved} pending checkout` : ""}
														{remaining !== null ? ` · ${remaining} left` : ""}
													</div>
												)}
											</div>
										);
									})}
									<button type="button" className="adm-ghost-btn" onClick={addTier} style={{ alignSelf: "flex-start" }}>
										+ Add ticket standard
									</button>
								</div>
							</div>
						</div>
					</div>

					{saving && coverFile && (
						<div className="adm-upload-progress">
							<div className="adm-upload-progress__bar" style={{ width: `${uploadProgress}%` }} />
							<span>Picture {uploadProgress}%</span>
						</div>
					)}

					<div className="adm-book-form__actions">
						<button className="adm-ghost-btn" onClick={resetForm} disabled={saving}>Cancel</button>
						<button className="adm-primary-btn" onClick={handleSubmit} disabled={saving}>
							{saving ? "Saving…" : editEvent ? "Save changes" : "Publish event"}
						</button>
					</div>
				</div>
			)}

			{loading ? (
				<div className="adm-bookings-empty">Loading events…</div>
			) : events.length === 0 ? (
				<div className="adm-bookings-empty">
					<div className="adm-bookings-empty__icon">🎟️</div>
					<div>No events yet. Add your first event above.</div>
				</div>
			) : (
				<div className="adm-book-list">
					{events.map(ev => (
						<div key={ev.id} className={`adm-book-card${!ev.available ? " adm-book-card--unavailable" : ""}`}>
							{ev.coverUrl
								? <img className="adm-book-card__cover" src={ev.coverUrl} alt={ev.title} />
								: <div className="adm-book-card__cover adm-book-card__cover--placeholder">🎫</div>
							}
							<div className="adm-book-card__body">
								<div className="adm-book-card__title">{ev.title}</div>
								<div className="adm-book-card__author">
									{ev.date && formatDisplayDate(ev.date)}{ev.time ? ` · ${ev.time}` : ""}
									{ev.location ? ` · ${ev.location}` : ""}
								</div>
								{ev.description && <div className="adm-book-card__desc">{ev.description}</div>}
								<div className="adm-book-card__footer" style={{ flexWrap: "wrap", gap: 6 }}>
									{ev.basePrice ? (
										<span className="adm-book-card__price">{formatCurrency(ev.basePrice)}</span>
									) : null}
									{(ev.ticketTiers || []).map(t => {
										const remaining = remainingFor(t);
										const soldOut = remaining !== null && remaining <= 0;
										return (
											<span
												key={t.id}
												className={`adm-badge ${soldOut ? "adm-badge--muted" : "adm-badge--blue"}`}
											>
												{t.name}: {Number(t.price) === 0 ? "Free" : formatCurrency(t.price)}
												{remaining !== null ? ` · ${soldOut ? "sold out" : `${remaining} left`}` : ""}
											</span>
										);
									})}
									{!ev.available && <span className="adm-badge adm-badge--muted">Unlisted</span>}
								</div>
							</div>
							<div className="adm-book-card__actions">
								<button className="adm-icon-btn" title="Edit" onClick={() => openEdit(ev)}>✏️</button>
								<button className="adm-icon-btn adm-icon-btn--danger" title="Delete" onClick={() => handleDelete(ev)}>🗑</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const CommercialPanel = () => {
	const currentMonth = new Date().toISOString().slice(0, 7);
	const [month, setMonth] = useState(currentMonth);
	const [view, setView] = useState("books");
	const [books, setBooks] = useState([]);
	const [events, setEvents] = useState([]);
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const [year, monthNumber] = month.split("-").map(Number);
		const start = new Date(year, monthNumber - 1, 1);
		const end = new Date(year, monthNumber, 1);
		setLoading(true);
		setError(null);

		Promise.all([
			getDocs(query(collection(db, "bookOrders"), where("createdAt", ">=", start), where("createdAt", "<", end))),
			getDocs(query(collection(db, "ticketOrders"), where("createdAt", ">=", start), where("createdAt", "<", end))),
			getDocs(query(collection(db, "bookings"), where("paidAt", ">=", start), where("paidAt", "<", end))),
		]).then(([bookSnap, eventSnap, bookingSnap]) => {
			if (cancelled) return;
			setBooks(bookSnap.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => item.status === "paid"));
			setEvents(eventSnap.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => item.status === "paid"));
			setBookings(bookingSnap.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => item.paymentStatus === "paid"));
		}).catch((err) => {
			if (!cancelled) setError(err.message || "Could not load commercial data.");
		}).finally(() => {
			if (!cancelled) setLoading(false);
		});

		return () => { cancelled = true; };
	}, [month]);

	const activeOrders = view === "books" ? books : view === "events" ? events : bookings;
	const totalCents = activeOrders.reduce((sum, order) => sum + Number(order.amountCents || 0), 0);
	const quantity = activeOrders.reduce((sum, order) => sum + Number(order.quantity || 1), 0);
	const currency = activeOrders[0]?.currency || "ZAR";
	const formatMoney = (cents) => new Intl.NumberFormat("en-ZA", { style: "currency", currency, maximumFractionDigits: 0 }).format(cents / 100);
	const physicalOrders = books.filter((order) => order.format === "hardCopy");
	const formatPaidDate = (value) => {
		if (!value) return "Paid";
		const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
		return Number.isNaN(date.getTime()) ? "Paid" : date.toLocaleDateString("en-ZA");
	};
	const setDeliveryStatus = async (order, deliveryStatus) => {
		try {
			await updateDoc(doc(db, "bookOrders", order.id), { deliveryStatus, deliveryUpdatedAt: serverTimestamp() });
			setBooks((current) => current.map((item) => item.id === order.id ? { ...item, deliveryStatus } : item));
			if (order.deliveryStatus !== deliveryStatus) {
				try {
					await sendBookDeliveryUpdate(order, deliveryStatus);
				} catch (emailError) {
					setError(`Delivery status was updated, but the buyer email could not be sent: ${emailError.message}`);
				}
			}
		} catch (err) {
			setError(err.message || "Could not update the delivery status.");
		}
	};

	return (
		<section className="adm-commercial">
			<div className="adm-commercial__header">
				<div><div className="adm-panel__eyebrow">Sales overview</div><h1>Commercial</h1><p>Monthly paid purchases across the store, events, and appointments.</p></div>
				<label className="adm-commercial__month">Month<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
			</div>
			<div className="adm-commercial__tabs" role="tablist" aria-label="Commercial purchase type">
				<button type="button" role="tab" aria-selected={view === "books"} className={view === "books" ? "is-active" : ""} onClick={() => setView("books")}><BookOpen size={16} /> Books</button>
				<button type="button" role="tab" aria-selected={view === "events"} className={view === "events" ? "is-active" : ""} onClick={() => setView("events")}><Ticket size={16} /> Events</button>
				<button type="button" role="tab" aria-selected={view === "bookings"} className={view === "bookings" ? "is-active" : ""} onClick={() => setView("bookings")}><CalendarDays size={16} /> Bookings</button>
			</div>
			{error && <div className="adm-query-error" role="alert">{error}</div>}
			{loading ? <div className="adm-commercial__empty">Loading purchases…</div> : (
				<>
					<div className="adm-commercial__stats">
						<div><span>Revenue</span><strong>{formatMoney(totalCents)}</strong></div>
						<div><span>{view === "bookings" ? "Paid bookings" : "Paid orders"}</span><strong>{activeOrders.length}</strong></div>
						<div><span>{view === "books" ? "Formats sold" : view === "events" ? "Tickets sold" : "Appointments"}</span><strong>{quantity}</strong></div>
					</div>
					{activeOrders.length === 0 ? <div className="adm-commercial__empty">{view === "bookings" ? "No paid appointment bookings for this month." : `No paid ${view} purchases for this month.`}</div> : (
						<div className="adm-commercial__table-wrap"><table className="adm-commercial__table"><thead><tr><th>Customer</th><th>{view === "books" ? "Book / format" : view === "events" ? "Event / tier" : "Service / appointment"}</th><th>Paid</th><th>Amount</th></tr></thead><tbody>
							{[...activeOrders].sort((a, b) => {
								const dateValue = (item) => (view === "bookings" ? item.paidAt : item.createdAt)?.seconds || 0;
								return dateValue(b) - dateValue(a);
							}).map((order) => {
								const customerName = view === "bookings"
									? [order.name, order.surname].filter(Boolean).join(" ")
									: [order.buyerName, order.buyerSurname].filter(Boolean).join(" ");
								const customerEmail = view === "bookings" ? order.email : order.buyerEmail;
								const purchaseName = view === "books" ? order.bookTitle : view === "events" ? order.eventTitle : order.serviceTitle;
								const purchaseDetail = view === "books"
									? (order.format === "hardCopy" ? "Hard copy" : "Online PDF")
									: view === "events"
										? `${order.tierName || "Ticket"} · ${order.quantity || 1}`
										: `${order.servicePackageLabel || "Appointment"} · ${order.date || ""}${order.time ? ` at ${order.time}` : ""}`;
								return (
									<tr key={order.id}><td><strong>{customerName || "Unnamed customer"}</strong><small>{customerEmail || "No email"}</small></td><td><strong>{purchaseName || (view === "bookings" ? "Appointment" : "Purchase")}</strong><small>{purchaseDetail}</small></td><td>{formatPaidDate(view === "bookings" ? order.paidAt : order.paidAt || order.createdAt)}</td><td><strong>{formatMoney(order.amountCents || 0)}</strong></td></tr>
								);
							})}
						</tbody></table></div>
					)}
					{view === "books" && physicalOrders.length > 0 && (
						<section className="adm-delivery-queue">
							<div className="adm-delivery-queue__heading">
								<div><div className="adm-panel__eyebrow">Fulfilment</div><h2>Hard-copy delivery queue</h2><p>Customer contact and delivery details for paid book orders.</p></div>
								<span className="adm-delivery-queue__count">{physicalOrders.length} to manage</span>
							</div>
							<div className="adm-delivery-grid">
								{physicalOrders.map((order) => {
									const deliveryStatus = order.deliveryStatus || "new";
									return <article className="adm-delivery-card" key={order.id}>
										<div className="adm-delivery-card__top"><span className={`adm-delivery-status is-${deliveryStatus}`}>{deliveryStatus === "dispatched" ? "Dispatched" : deliveryStatus === "stock_issue" ? "Stock check needed" : "Ready to dispatch"}</span><strong>{formatMoney(order.amountCents || 0)}</strong></div>
										<h3>{order.bookTitle || "Book order"}</h3>
										<div className="adm-delivery-card__customer"><strong>{[order.buyerName, order.buyerSurname].filter(Boolean).join(" ") || "Unnamed customer"}</strong><a href={`mailto:${order.buyerEmail}`}>{order.buyerEmail || "No email"}</a><a href={`tel:${String(order.buyerPhone || "").replace(/\s/g, "")}`}>{order.buyerPhone || "No cellphone"}</a></div>
										<div className="adm-delivery-card__address"><span>Delivery address</span><p>{order.buyerAddress || "No address captured"}</p></div>
										<div className="adm-delivery-card__actions"><button className="adm-ghost-btn" type="button" onClick={() => navigator.clipboard?.writeText(`${[order.buyerName, order.buyerSurname].filter(Boolean).join(" ")}\n${order.buyerPhone || ""}\n${order.buyerEmail || ""}\n${order.buyerAddress || ""}`)}>Copy details</button>{deliveryStatus !== "dispatched" && <button className="adm-primary-btn" type="button" onClick={() => setDeliveryStatus(order, "dispatched")}>Mark dispatched</button>}</div>
									</article>;
								})}
							</div>
						</section>
					)}
				</>
			)}
		</section>
	);
};

function prettyBucketName(bucket) {
	return bucket.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Ranked "which bucket is heaviest" view — a bar per bucket, largest first.
const StorageBucketBars = ({ buckets }) => {
	const withData = buckets.filter((b) => b.bytes > 0);
	if (!withData.length) return <div className="adm-chart-empty">No files uploaded yet</div>;
	const max = Math.max(...withData.map((b) => b.bytes), 1);
	return (
		<div className="adm-service-bars">
			{withData.map((b, i) => (
				<div key={b.bucket} className="adm-service-bar-row">
					<span className="adm-service-bar-label">{prettyBucketName(b.bucket)}</span>
					<div className="adm-service-bar-track">
						<div
							className="adm-service-bar-fill"
							style={{ width: `${(b.bytes / max) * 100}%`, background: SERVICE_CHART_COLORS[i % SERVICE_CHART_COLORS.length] }}
						/>
					</div>
					<span className="adm-service-bar-count">{formatBytes(b.bytes)}</span>
				</div>
			))}
		</div>
	);
};

// "Share of the whole" view — same data, composition donut so it reads at a glance.
const StorageCompositionDonut = ({ buckets }) => {
	const breakdown = buckets.filter((b) => b.bytes > 0).map((b) => [prettyBucketName(b.bucket), b.bytes]);
	if (!breakdown.length) return <div className="adm-chart-empty">No files uploaded yet</div>;
	const total = breakdown.reduce((s, [, bytes]) => s + bytes, 0);
	const CX = 72, CY = 72, R = 58;
	return (
		<div className="adm-donut-wrap">
			<svg viewBox="0 0 144 144" width="144" height="144" aria-label="Storage composition by bucket">
				<PieSlices breakdown={breakdown} total={total} cx={CX} cy={CY} r={R} />
				<text x={CX} y={CY - 6} textAnchor="middle" fontSize="15" fontWeight="600" fontFamily="var(--font-display)" fill="var(--adm-ink)">{formatBytes(total)}</text>
				<text x={CX} y={CY + 13} textAnchor="middle" fontSize="10" fill="var(--adm-ink-faint)">used</text>
			</svg>
			<div className="adm-donut-legend">
				{breakdown.map(([label, bytes], i) => (
					<div key={label} className="adm-legend-row">
						<span className="adm-legend-dot" style={{ background: SERVICE_CHART_COLORS[i % SERVICE_CHART_COLORS.length] }} />
						<span className="adm-legend-label">{label}</span>
						<span className="adm-legend-count">{formatBytes(bytes)}</span>
					</div>
				))}
			</div>
		</div>
	);
};

// Compact top-bar pill: click it to open the full breakdown in a modal.
const StorageIndicator = () => {
	const [buckets, setBuckets] = useState([]);
	const [usedBytes, setUsedBytes] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);

	const loadUsage = async () => {
		setLoading(true);
		setError(null);
		try {
			const bucketResults = await Promise.all(STORAGE_BUCKETS.map(async (bucket) => {
				let offset = 0;
				let bytes = 0;
				let fileCount = 0;
				while (true) {
					const { data, error: listError } = await supabase.storage
						.from(bucket)
						.list("", { limit: 1000, offset, sortBy: { column: "name", order: "asc" } });
					if (listError) throw new Error(`${bucket}: ${listError.message}`);
					bytes += (data || []).reduce((sum, file) => sum + Number(file.metadata?.size || 0), 0);
					fileCount += (data || []).length;
					if (!data || data.length < 1000) break;
					offset += data.length;
				}
				return { bucket, bytes, fileCount };
			}));
			setBuckets(bucketResults);
			setUsedBytes(bucketResults.reduce((sum, b) => sum + b.bytes, 0));
		} catch (err) {
			setError(err.message || "Storage usage could not be loaded.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadUsage();
	}, []);

	useEffect(() => {
		if (!modalOpen) return;
		const onKeyDown = (e) => { if (e.key === "Escape") setModalOpen(false); };
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [modalOpen]);

	const remainingBytes = Math.max(TRACKED_STORAGE_LIMIT - usedBytes, 0);
	const usedPercent = Math.min((usedBytes / TRACKED_STORAGE_LIMIT) * 100, 100);
	const isNearLimit = usedPercent >= 80;
	const rankedBuckets = [...buckets].sort((a, b) => b.bytes - a.bytes);

	return (
		<>
			<button
				type="button"
				className={`adm-storage-pill${isNearLimit ? " adm-storage-pill--warning" : ""}`}
				onClick={() => setModalOpen(true)}
				aria-haspopup="dialog"
				aria-expanded={modalOpen}
				title="View Supabase storage usage"
			>
				<Database size={15} strokeWidth={2.2} aria-hidden="true" />
				<span className="adm-storage-pill__track" role="progressbar" aria-valuenow={Math.round(usedPercent)} aria-valuemin="0" aria-valuemax="100">
					<span className="adm-storage-pill__fill" style={{ width: `${loading ? 0 : usedPercent}%` }} />
				</span>
				<span className="adm-storage-pill__pct">{loading ? "…" : `${Math.round(usedPercent)}%`}</span>
			</button>

			{modalOpen && (
				<div className="adm-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setModalOpen(false); }}>
					<div className="adm-storage-modal" role="dialog" aria-modal="true" aria-labelledby="storage-modal-title">
						<div className="adm-hours-modal__header">
							<div>
								<div className="adm-modal-eyebrow">Storage health</div>
								<h3 id="storage-modal-title">Supabase storage</h3>
								<p>Tracked across your app's upload buckets</p>
							</div>
							<button type="button" className="adm-modal-close" onClick={() => setModalOpen(false)} aria-label="Close storage usage">
								<X size={18} aria-hidden="true" />
							</button>
						</div>

						<div className="adm-storage-modal__body">
							{error ? (
								<div className="adm-storage-panel__error" role="alert">
									{error} Check bucket list permissions in Supabase.
								</div>
							) : (
								<>
									<div className="adm-storage-modal__bar-row">
										<div className="adm-storage-panel__bar adm-storage-panel__bar--lg" role="progressbar" aria-valuenow={Math.round(usedPercent)} aria-valuemin="0" aria-valuemax="100">
											<div className={`adm-storage-panel__fill${isNearLimit ? " adm-storage-panel__fill--warning" : ""}`} style={{ width: `${usedPercent}%` }} />
										</div>
										<button className="adm-ghost-btn adm-storage-modal__refresh" type="button" onClick={loadUsage} disabled={loading}>
											{loading ? "Checking…" : "Refresh"}
										</button>
									</div>
									<div className="adm-storage-panel__stats">
										<span>{loading ? "Checking usage…" : `${formatBytes(usedBytes)} tracked`}</span>
										<strong>{loading ? "—" : `${formatBytes(remainingBytes)} remaining`}</strong>
									</div>
									<p className="adm-storage-panel__note">Estimate against a 1 GB tracking budget. Confirm your actual plan quota in Supabase.</p>

									<div className="adm-storage-modal__charts">
										<div className="adm-panel adm-panel--chart adm-panel--nested">
											<div className="adm-panel__heading">
												<div className="adm-panel__heading-left">Heaviest buckets</div>
											</div>
											<div className="adm-chart-body">
												<StorageBucketBars buckets={rankedBuckets} />
											</div>
										</div>
										<div className="adm-panel adm-panel--chart adm-panel--nested">
											<div className="adm-panel__heading">
												<div className="adm-panel__heading-left">Composition</div>
											</div>
											<div className="adm-chart-body">
												<StorageCompositionDonut buckets={buckets} />
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

// ── User management ────────────────────────────────────────────────────────────
//
// Client-side note: the Firebase Auth SDK can only create a user by signing
// in as them on whichever app instance makes the call — there's no "create
// another user without switching sessions" method available in the browser.
// The workaround here spins up a second, throwaway Firebase app instance,
// creates the account on *that* instance (so the signed-in admin's own
// session in the main app is untouched), then tears the throwaway instance
// down. A `adminUsers` Firestore collection mirrors who's been added so this
// tab has something to list — the Auth SDK has no client-side "list users".
// Removing someone here only removes that directory entry, not their actual
// login; revoking access has to happen from the Firebase console (or a
// Cloud Function, for a fuller admin experience).

const UserManagementTab = ({ currentAdminEmail, currentAdminUid, canManageUsers }) => {
	const [admins, setAdmins] = useState([]);
	const [loadingList, setLoadingList] = useState(true);
	const [listError, setListError] = useState(null);

	const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
	const [showPassword, setShowPassword] = useState(false);
	const [creating, setCreating] = useState(false);
	const [formError, setFormError] = useState(null);
	const [successMsg, setSuccessMsg] = useState(null);

	useEffect(() => {
		const unsub = onSnapshot(
			collection(db, "adminUsers"),
			(snap) => {
				const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
				docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
				setAdmins(docs);
				setLoadingList(false);
			},
			(err) => { setListError(err.message); setLoadingList(false); }
		);
		return unsub;
	}, []);

	const updateField = (field, value) => {
		setForm((f) => ({ ...f, [field]: value }));
		setFormError(null);
	};

	const handleGeneratePassword = () => {
		const pwd = generateSecurePassword();
		setForm((f) => ({ ...f, password: pwd, confirmPassword: pwd }));
		setShowPassword(true);
	};

	const resetForm = () => {
		setForm({ name: "", email: "", password: "", confirmPassword: "" });
		setShowPassword(false);
		setFormError(null);
	};

	const handleCreate = async (e) => {
		e.preventDefault();
		if (!canManageUsers) return;
		setFormError(null);
		setSuccessMsg(null);

		const name = form.name.trim();
		const email = form.email.trim();

		if (!name) return setFormError("Enter the team member's name.");
		if (!/^\S+@\S+\.\S+$/.test(email)) return setFormError("Enter a valid email address.");
		if (form.password.length < 6) return setFormError("Password must be at least 6 characters.");
		if (form.password !== form.confirmPassword) return setFormError("Passwords don't match.");
		if (!firebaseConfig) return setFormError("Missing firebaseConfig export from ../firebase — see setup note.");

		setCreating(true);
		let secondaryApp;
		try {
			secondaryApp = initializeApp(firebaseConfig, `admin-create-${Date.now()}`);
			const secondaryAuth = getAuth(secondaryApp);
			const cred = await createUserWithEmailAndPassword(secondaryAuth, email, form.password);
			await updateProfile(cred.user, { displayName: name });
			await setDoc(doc(db, "adminUsers", cred.user.uid), {
				uid: cred.user.uid,
				name,
				email,
				role: "team",
				isActive: true,
				createdAt: serverTimestamp(),
				createdBy: currentAdminEmail || null,
			});
			await signOut(secondaryAuth);
			setSuccessMsg(`${name}'s team account is ready — share the password with them directly.`);
			resetForm();
		} catch (err) {
			setFormError(friendlyAuthError(err));
		} finally {
			if (secondaryApp) {
				try { await deleteApp(secondaryApp); } catch { /* best-effort cleanup */ }
			}
			setCreating(false);
		}
	};

	const handleRemove = async (admin) => {
		if (!canManageUsers || admin.role !== "team") return;
		if (!window.confirm(`Remove ${admin.name || admin.email} from dashboard access? Their Firebase sign-in will remain, but they will no longer be able to open the admin dashboard.`)) return;
		try {
			await updateDoc(doc(db, "adminUsers", admin.id), {
				isActive: false,
				updatedAt: serverTimestamp(),
			});
		} catch (err) {
			setListError(err.message);
		}
	};

	return (
		<div className="adm-users-tab">
			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">User management</div>
					<div className="adm-books-sub">{admins.length} account{admins.length !== 1 ? "s" : ""} in the dashboard roster</div>
				</div>
			</div>

			{!canManageUsers && (
				<div className="adm-notif-banner adm-notif-banner--muted" role="note">
					You can view the dashboard roster. Only superadmins can add or remove team accounts.
				</div>
			)}

			<div className={`adm-users-grid${canManageUsers ? "" : " adm-users-grid--readonly"}`}>
				{canManageUsers && <form className="adm-panel adm-user-form" onSubmit={handleCreate}>
					<div className="adm-panel__heading">
						<div className="adm-panel__heading-left"><UserPlus size={16} aria-hidden="true" /> Add a team member</div>
					</div>
					<p className="adm-storage-panel__note">New accounts receive operational team access only. Existing accounts on this Firebase admin roster keep superadmin access.</p>

					{formError && <div className="adm-query-error" role="alert">{formError}</div>}
					{successMsg && <div className="adm-user-form__success" role="status"><ShieldCheck size={15} aria-hidden="true" /> {successMsg}</div>}

					<div className="adm-field-row">
						<label className="adm-field-label">Full name</label>
						<input
							className="adm-field-input"
							value={form.name}
							onChange={(e) => updateField("name", e.target.value)}
							placeholder="Jane Zwane"
							autoComplete="off"
						/>
					</div>

					<div className="adm-field-row">
						<label className="adm-field-label">Email</label>
						<input
							className="adm-field-input"
							type="email"
							value={form.email}
							onChange={(e) => updateField("email", e.target.value)}
							placeholder="jane@example.com"
							autoComplete="off"
						/>
					</div>

					<div className="adm-field-row">
						<label className="adm-field-label">Password</label>
						<div className="adm-password-field">
							<KeyRound size={15} className="adm-password-field__icon" aria-hidden="true" />
							<input
								className="adm-field-input adm-password-field__input"
								type={showPassword ? "text" : "password"}
								value={form.password}
								onChange={(e) => updateField("password", e.target.value)}
								placeholder="At least 6 characters"
								autoComplete="new-password"
							/>
							<button type="button" className="adm-password-field__toggle" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
								{showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
							</button>
						</div>
						<button type="button" className="adm-text-btn" onClick={handleGeneratePassword}>Generate a secure password</button>
					</div>

					<div className="adm-field-row">
						<label className="adm-field-label">Confirm password</label>
						<input
							className="adm-field-input"
							type={showPassword ? "text" : "password"}
							value={form.confirmPassword}
							onChange={(e) => updateField("confirmPassword", e.target.value)}
							placeholder="Re-enter password"
							autoComplete="new-password"
						/>
					</div>

					<div className="adm-book-form__actions">
						<button type="button" className="adm-ghost-btn" onClick={resetForm} disabled={creating}>Clear</button>
						<button type="submit" className="adm-primary-btn" disabled={creating}>
							{creating ? "Creating account…" : "Create account"}
						</button>
					</div>
				</form>}

				<div className="adm-panel adm-user-list-panel">
					<div className="adm-panel__heading">
						<div className="adm-panel__heading-left">Team members</div>
					</div>
					{listError && <div className="adm-query-error" role="alert">{listError}</div>}
					{loadingList ? (
						<div className="adm-bookings-empty">Loading team members…</div>
					) : admins.length === 0 ? (
						<div className="adm-bookings-empty">
							<div className="adm-bookings-empty__icon">👤</div>
							<div>No team members added yet.</div>
						</div>
					) : (
						<div className="adm-user-list">
							{admins.map((admin) => {
								const isTeamAccount = admin.role === "team";
								const cannotRemove = !isTeamAccount || admin.id === currentAdminUid || admin.isActive === false;
								return (
									<div className="adm-user-row" key={admin.id}>
										<div className={`adm-customer-avatar adm-customer-avatar--${avatarColorFor(admin.name || admin.email)}`}>
											{initialsFor(admin.name) || "?"}
										</div>
										<div className="adm-user-row__body">
											<div className="adm-user-row__name">{admin.name || "Unnamed"}</div>
											<div className="adm-user-row__email">{admin.email}</div>
											<div className="adm-user-row__meta">
												{isTeamAccount ? admin.isActive === false ? "Team access removed" : "Team access" : "Superadmin access"}
												{" · "}
												Added {admin.createdAt?.toDate ? admin.createdAt.toDate().toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : "recently"}
												{admin.createdBy && <> · by {admin.createdBy}</>}
											</div>
										</div>
										{canManageUsers && (
											<button className="adm-icon-btn adm-icon-btn--danger" title={cannotRemove ? isTeamAccount ? "Team access already removed" : "Firebase Auth accounts are superadmins" : "Remove dashboard access"} onClick={() => handleRemove(admin)} disabled={cannotRemove}>
												<Trash2 size={15} />
											</button>
										)}
										</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Sidebar ────────────────────────────────────────────────────────────────────

const Sidebar = ({ tabs, activeTab, setActiveTab, collapsed, setCollapsed }) => (
	<aside className={`adm-sidebar${collapsed ? " adm-sidebar--collapsed" : ""}`}>
		<div className="adm-sidebar__brand">
			<div className="adm-sidebar__logo">JZ</div>
			{!collapsed && (
				<div className="adm-sidebar__brand-text">
					<div className="adm-sidebar__brand-title">Jabu Zwane</div>
					<div className="adm-sidebar__brand-sub">Admin Dashboard</div>
				</div>
			)}
		</div>

		<nav className="adm-sidebar__nav" aria-label="Admin sections">
			{tabs.map(t => {
				const Icon = t.icon;
				const active = activeTab === t.id;
				return (
					<button
						key={t.id}
						type="button"
						className={`adm-sidebar__item${active ? " adm-sidebar__item--active" : ""}`}
						onClick={() => {
							setActiveTab(t.id);
							window.scrollTo({
								top: 0,
								behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
							});
						}}
						title={t.label}
						aria-label={t.label}
						aria-current={active ? "page" : undefined}
						aria-controls="admin-main-content"
					>
						<span className="adm-sidebar__item-icon" aria-hidden="true"><Icon size={17} strokeWidth={2.2} /></span>
						<span className="adm-sidebar__item-label">{t.label}</span>
						{active && <span className="adm-sidebar__item-bar" />}
					</button>
				);
			})}
		</nav>

		<button
			type="button"
			className="adm-sidebar__collapse-btn"
			onClick={() => setCollapsed(c => !c)}
			title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
			aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
			aria-expanded={!collapsed}
		>
			<ChevronLeft size={16} strokeWidth={2.2} style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
			{!collapsed && <span>Collapse</span>}
		</button>
	</aside>
);

// ── Topbar ─────────────────────────────────────────────────────────────────────

const DailyBusinessBrief = ({ bookings, loading, onOpenBookings }) => {
	const today = toISODate(new Date());
	const pendingCount = bookings.filter((booking) => (booking.status || "pending") === "pending").length;
	const todayCount = bookings.filter(
		(booking) => booking.date === today && booking.status !== "cancelled"
	).length;

	return (
		<section
			className={`adm-daily-brief${pendingCount ? " adm-daily-brief--attention" : ""}`}
			aria-label="Daily business briefing"
			aria-live="polite"
		>
			<div className="adm-daily-brief__icon" aria-hidden="true">
				{pendingCount ? <AlertCircle size={18} /> : <Sparkles size={18} />}
			</div>
			<div className="adm-daily-brief__copy">
				<strong>{loading ? "Preparing today’s business brief…" : pendingCount
					? `${pendingCount} booking${pendingCount === 1 ? "" : "s"} need review`
					: "No bookings waiting for review"}</strong>
				<span>
					{loading
						? "Checking the latest booking activity."
						: `Today's schedule: ${todayCount} appointment${todayCount === 1 ? "" : "s"}.`}
				</span>
			</div>
			<button
				type="button"
				className="adm-daily-brief__action"
				onClick={onOpenBookings}
				disabled={loading}
			>
				Review bookings
				<ChevronRight size={15} aria-hidden="true" />
			</button>
		</section>
	);
};

const Topbar = ({ email, activeLabel, onSignOut, theme, onToggleTheme, isAdmin, onOpenMarketingStrategy }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const menuRef = useRef();

	useEffect(() => {
		const onClickOutside = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
		};
		const onKeyDown = (e) => {
			if (e.key === "Escape") setMenuOpen(false);
		};
		document.addEventListener("mousedown", onClickOutside);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onClickOutside);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, []);

	const fullName = nameFromEmail(email);
	const firstName = fullName.split(" ")[0];
	const todayLabel = new Date().toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });

	return (
		<header className="adm-topbar">
			<div className="adm-topbar__left">
				<h1 className="adm-topbar__title">{activeLabel}</h1>
				<div className="adm-topbar__sub">{timeGreeting()}, {firstName}</div>
			</div>

			<div className="adm-topbar__right">
				<StorageIndicator />

				{isAdmin && (
					<button
						type="button"
						className="adm-primary-btn adm-ai-agent-btn"
						onClick={onOpenMarketingStrategy}
						aria-label="Open Marketing AI"
					>
						<Sparkles size={16} aria-hidden="true" />
						<span>Marketing AI</span>
					</button>
				)}

				<button
					type="button"
					className="adm-theme-toggle"
					onClick={onToggleTheme}
					aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
					title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
					aria-pressed={theme === "dark"}
				>
					{theme === "dark"
						? <Sun size={16} strokeWidth={2.2} aria-hidden="true" />
						: <Moon size={16} strokeWidth={2.2} aria-hidden="true" />}
					<span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
				</button>

				<div className="adm-topbar__date">
					<CalendarDays size={15} strokeWidth={2.2} aria-hidden="true" />
					<span>{todayLabel}</span>
				</div>

				<div className="adm-topbar__account" ref={menuRef}>
					<button
						type="button"
						className="adm-avatar-btn"
						onClick={() => setMenuOpen(o => !o)}
						aria-haspopup="menu"
						aria-expanded={menuOpen}
					>
						<span className="adm-avatar">{initialsFromEmail(email)}</span>
						<span className="adm-avatar-btn__text">
							<span className="adm-avatar-btn__name">{fullName}</span>
							<span className="adm-avatar-btn__email">{email || "—"}</span>
						</span>
						<ChevronDown size={14} strokeWidth={2.2} style={{ transform: menuOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
					</button>

					{menuOpen && (
						<div className="adm-avatar-menu" role="menu">
							<div className="adm-avatar-menu__header">
								<span className="adm-avatar adm-avatar--lg">{initialsFromEmail(email)}</span>
								<div>
									<div className="adm-avatar-menu__name">{fullName}</div>
									<div className="adm-avatar-menu__email">{email || "—"}</div>
								</div>
							</div>
							<button type="button" role="menuitem" className="adm-avatar-menu__signout" onClick={onSignOut}>
								<LogOut size={14} strokeWidth={2.2} />
								Sign out
							</button>
						</div>
					)}
				</div>
			</div>
		</header>
	);
};

const MarketingStrategyModal = ({ snapshot, onClose, theme }) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [strategy, setStrategy] = useState(null);
	const closeButtonRef = useRef(null);

	const generateStrategy = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const currentUser = auth.currentUser;
			if (!currentUser) throw new Error("Sign in to the admin dashboard to generate a marketing strategy.");
			const firebaseIdToken = await currentUser.getIdToken();
			const { data, error: invokeError } = await supabase.functions.invoke(
				"generate-marketing-strategy",
				{
					body: {
						snapshot,
						firebaseIdToken,
						firebaseApiKey: firebaseConfig.apiKey,
					},
				}
			);
			if (invokeError) throw invokeError;
			if (data?.error) throw new Error(data.error);
			if (!data?.headline || !Array.isArray(data.actionSteps)) {
				throw new Error("The AI returned an incomplete strategy. Please try again.");
			}
			setStrategy(data);
		} catch (err) {
			console.error("Could not generate marketing strategy:", err);
			setError(err.name === "FunctionsFetchError"
				? "Could not connect to the marketing strategy service. Please check your connection and try again."
				: err.message || "Could not generate a strategy right now. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [snapshot]);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		closeButtonRef.current?.focus();
		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		generateStrategy();
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [generateStrategy, onClose]);

	return createPortal(
		<div className="adm-strategy-backdrop" data-theme={theme} role="presentation" onMouseDown={(event) => {
			if (event.target === event.currentTarget) onClose();
		}}>
			<section className="adm-strategy-modal" role="dialog" aria-modal="true" aria-labelledby="adm-strategy-title">
				<header className="adm-strategy-modal__header">
					<div className="adm-strategy-modal__brand">
						<span className="adm-strategy-modal__icon"><Sparkles size={19} /></span>
						<div>
							<div className="adm-strategy-modal__eyebrow">Groq AI · Marketing planner</div>
							<h2 id="adm-strategy-title">Your next growth move</h2>
						</div>
					</div>
					<button ref={closeButtonRef} type="button" className="adm-modal-close" onClick={onClose} aria-label="Close marketing strategy">
						<X size={17} />
					</button>
				</header>

				<div className="adm-strategy-modal__body" aria-live="polite">
					{loading ? (
						<div className="adm-strategy-state">
							<span className="adm-strategy-spinner" aria-hidden="true" />
							<strong>Finding a practical marketing opportunity…</strong>
							<p>Reviewing booking trends, popular services, and referral channels.</p>
						</div>
					) : error ? (
						<div className="adm-strategy-error" role="alert">
							<strong>Strategy unavailable</strong>
							<p>{error}</p>
							<button type="button" className="adm-primary-btn" onClick={generateStrategy}>
								<Target size={15} /> Try again
							</button>
						</div>
					) : strategy && (
						<div className="adm-strategy-result">
							<div className="adm-strategy-result__signal">
								<Target size={16} />
								<span>Based on booking data · {snapshot.period}</span>
							</div>
							<h3>{strategy.headline}</h3>
							<p className="adm-strategy-result__summary">{strategy.summary}</p>
							{strategy.audience && (
								<div className="adm-strategy-result__callout">
									<span>WHO TO REACH</span>
									<p>{strategy.audience}</p>
								</div>
							)}
							{strategy.actionSteps.length > 0 && (
								<div className="adm-strategy-result__steps">
									<h4>Recommended next steps</h4>
									{strategy.actionSteps.map((step, index) => (
										<div className="adm-strategy-step" key={`${index}-${step.title}`}>
											<span className="adm-strategy-step__number">{index + 1}</span>
											<div><strong>{step.title}</strong><p>{step.detail}</p></div>
										</div>
									))}
								</div>
							)}
							{strategy.successMetric && (
								<div className="adm-strategy-result__metric">
									<strong>Measure success</strong>
									<span>{strategy.successMetric}</span>
								</div>
							)}
							<p className="adm-strategy-disclaimer">AI-generated suggestion based on aggregate booking data. Review it against your goals before acting.</p>
						</div>
					)}
				</div>
				<footer className="adm-strategy-modal__footer">
					<span>Uses booking totals and trends only; no customer contact details are sent.</span>
					{strategy && !loading && (
						<button type="button" className="adm-secondary-btn" onClick={generateStrategy} disabled={loading}>
							<Sparkles size={14} /> Generate another
						</button>
					)}
				</footer>
			</section>
		</div>,
		document.body
	);
};

// ── Main Dashboard ────────────────────────────────────────────────────────────

const AdminDashboard = () => {
	const navigate = useNavigate();

	const [authReady, setAuthReady] = useState(false);
	const [queryError, setQueryError] = useState(null);
	const [adminTheme, setAdminTheme] = useState(() =>
		window.localStorage.getItem("jabuzwane-admin-theme") === "dark" ? "dark" : "light"
	);
	const [activeTab, setActiveTab] = useState("overview"); // overview | bookings | books | keynotes | media
	const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
	const [userEmail, setUserEmail] = useState(null);
	const [accessRole, setAccessRole] = useState(null);
	const [accessStatus, setAccessStatus] = useState("loading");
	const [bookingSearchQuery, setBookingSearchQuery] = useState("");
	const [searchedBookingTerm, setSearchedBookingTerm] = useState("");
	const [bookingSearchResults, setBookingSearchResults] = useState(null);
	const [bookingSearchLoading, setBookingSearchLoading] = useState(false);
	const [bookingSearchError, setBookingSearchError] = useState("");
	const [marketingSnapshot, setMarketingSnapshot] = useState(null);
	const [currentMonthAttentionBookings, setCurrentMonthAttentionBookings] = useState([]);
	const [loadingCurrentMonthAttention, setLoadingCurrentMonthAttention] = useState(false);

	// Chart type preferences


	// ── Booking push notifications ──────────────────────────────────────────
	// Foreground toast + native browser Notification (works while this tab is
	// open, even backgrounded). Does NOT wake a fully-closed browser — that
	// would require FCM + a server, which this free-tier setup avoids.
	const isTeamMember = accessRole === "team";
	const canReceivePurchaseAlerts = accessRole === "admin" || accessRole === "owner";
	const { permission, requestPermission, toast, dismissToast } = useBookingWatcher(
		accessRole === "team" ? "team" : canReceivePurchaseAlerts ? "admin" : null
	);

	const {
		permission: pushPermission,
		subscribed,
		supported: pushSupported,
		subscribe: subscribeToPush,
	} = usePushNotifications(userEmail);

	const handleQuickStatusChange = async (booking, newStatus) => {
		try {
			await updateDoc(doc(db, "bookings", booking.id), {
				status: newStatus,
				updatedAt: serverTimestamp(),
			});
			setBookingSearchResults((current) => current
				? current.map((item) => item.id === booking.id ? { ...item, status: newStatus } : item)
				: current);
			try {
				await syncBookingWithGoogleCalendar(booking.id);
			} catch (calendarErr) {
				console.error("Failed to update Google Calendar:", calendarErr);
				setQueryError("Booking updated, but it could not be updated in Google Calendar. Please reconnect Google Calendar and try again.");
			}
		} catch (err) {
			console.error("Failed to update status:", err);
			setQueryError("Couldn't update that booking's status. Please try again.");
		}
	};

	useEffect(() => {
		window.localStorage.setItem("jabuzwane-admin-theme", adminTheme);
	}, [adminTheme]);

	const handleDeleteBooking = async (booking) => {
		const customer = `${booking.name || ""} ${booking.surname || ""}`.trim() || "this customer";
		if (!window.confirm(`Permanently delete booking ${booking.id} for ${customer} (${booking.email || "no email"}) on ${booking.date || "unknown date"} at ${booking.time || "unknown time"}? This cannot be undone.`)) return;
		try {
			let calendarError = null;
			try {
				await syncBookingWithGoogleCalendar(booking.id, "remove");
			} catch (err) {
				calendarError = err;
				console.error("Failed to remove Google Calendar event:", err);
			}
			await deleteDoc(doc(db, "bookings", booking.id));
			setBookingSearchResults((current) => current
				? current.filter((item) => item.id !== booking.id)
				: current);
			if (calendarError) {
				setQueryError("Website booking deleted, but its Google Calendar event could not be removed. Please remove it from Google Calendar manually.");
			}
		} catch (err) {
			console.error("Failed to delete booking:", err);
			setQueryError("Couldn't delete that booking. Please try again.");
		}
	};

	const searchAllBookings = async (event) => {
		event.preventDefault();
		const needle = bookingSearchQuery.trim().toLowerCase();
		if (!needle) {
			setBookingSearchResults(null);
			setSearchedBookingTerm("");
			setBookingSearchError("");
			return;
		}

		setBookingSearchLoading(true);
		setBookingSearchError("");
		try {
			const snapshot = await getDocs(collection(db, "bookings"));
			const matches = snapshot.docs
				.map((item) => {
					const data = item.data();
					return {
						id: item.id,
						...(isTeamMember ? omitTeamRestrictedBookingFields(data) : data),
						status: data.status || "pending",
					};
				})
				.filter((booking) => [
					booking.id,
					booking.name,
					booking.surname,
					booking.email,
					booking.cellphone,
					booking.serviceTitle,
					booking.date,
					booking.time,
					booking.status,
				].some((value) => String(value || "").toLowerCase().includes(needle)))
				.sort((a, b) => `${b.date || ""} ${b.time || ""}`.localeCompare(`${a.date || ""} ${a.time || ""}`));
			setBookingSearchResults(matches);
			setSearchedBookingTerm(bookingSearchQuery.trim());
		} catch (err) {
			console.error("Could not search bookings:", err);
			setBookingSearchError(err.message || "Could not search bookings. Please try again.");
			setBookingSearchResults(null);
		} finally {
			setBookingSearchLoading(false);
		}
	};

	const clearBookingSearch = () => {
		setBookingSearchQuery("");
		setSearchedBookingTerm("");
		setBookingSearchResults(null);
		setBookingSearchError("");
	};

	// Auth gate
	useEffect(() => {
		let unsubscribeProfile = null;
		const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
			if (unsubscribeProfile) unsubscribeProfile();
			unsubscribeProfile = null;

			if (!user) {
				setAccessRole(null);
				setAccessStatus("loading");
				navigate("/admin", { replace: true });
				return;
			}

			setAuthReady(false);
			setAccessRole(null);
			setAccessStatus("loading");
			setUserEmail(user.email || "");
			unsubscribeProfile = onSnapshot(
				doc(db, "adminUsers", user.uid),
				(profile) => {
					if (!profile.exists()) {
						setAccessRole("admin");
						setAccessStatus("authorized");
						setAuthReady(true);
						return;
					}

					const { role, isActive } = profile.data();
					if (role === "team" && isActive === false) {
						setAccessRole(null);
						setAccessStatus("denied");
						setAuthReady(true);
						return;
					}

					const effectiveRole = role || "admin";
					if (!["admin", "owner", "team"].includes(effectiveRole)) {
						setAccessRole(null);
						setAccessStatus("denied");
						setAuthReady(true);
						return;
					}
					setAccessRole(effectiveRole);
					setAccessStatus("authorized");
					setAuthReady(true);
				},
				(error) => {
					setQueryError(`Could not verify dashboard access: ${error.message}`);
					setAccessRole(null);
					setAccessStatus("error");
					setAuthReady(true);
				}
			);
		});
		return () => {
			unsubscribeAuth();
			if (unsubscribeProfile) unsubscribeProfile();
		};
	}, [navigate]);

	useEffect(() => {
		if (accessRole === "team" && TEAM_RESTRICTED_TAB_IDS.has(activeTab)) {
			setActiveTab("overview");
		}
	}, [accessRole, activeTab]);

	const today = useMemo(() => {
		const d = new Date(); d.setHours(0, 0, 0, 0); return d;
	}, []);

	// Calendar state
	const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
	const [selectedDate, setSelectedDate] = useState(toISODate(today));
	const year = viewDate.getFullYear();
	const month = viewDate.getMonth();

	// Firestore — current month
	const [monthBookings, setMonthBookings] = useState([]);
	const [loadingMonth, setLoadingMonth] = useState(false);
	const viewingCurrentMonth = year === today.getFullYear() && month === today.getMonth();

	useEffect(() => {
		if (!authReady) return;
		setLoadingMonth(true); setQueryError(null); setMonthBookings([]);

		const q = query(collection(db, "bookings"), where("yearMonth", "==", toYearMonth(year, month)));
		const unsub = onSnapshot(q,
			(snap) => {
				const docs = snap.docs.map((d) => {
					const data = d.data();
					return {
						id: d.id,
						...(isTeamMember ? omitTeamRestrictedBookingFields(data) : data),
					};
				});
				docs.sort((a, b) => a.date !== b.date ? a.date.localeCompare(b.date) : a.time.localeCompare(b.time));
				setMonthBookings(docs);
				setLoadingMonth(false);
			},
			(err) => { setQueryError(err.message); setLoadingMonth(false); }
		);
		return () => unsub();
	}, [authReady, isTeamMember, year, month]);

	useEffect(() => {
		if (!authReady || viewingCurrentMonth) {
			setCurrentMonthAttentionBookings([]);
			setLoadingCurrentMonthAttention(false);
			return undefined;
		}

		setCurrentMonthAttentionBookings([]);
		setLoadingCurrentMonthAttention(true);
		const currentYearMonth = toYearMonth(today.getFullYear(), today.getMonth());
		const q = query(collection(db, "bookings"), where("yearMonth", "==", currentYearMonth));
		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				setCurrentMonthAttentionBookings(snapshot.docs.map((bookingDoc) => {
					const data = bookingDoc.data();
					return { date: data.date, status: data.status || "pending" };
				}));
				setLoadingCurrentMonthAttention(false);
			},
			(error) => {
				console.error("Could not load the daily business brief:", error);
				setQueryError(`Could not load the daily business brief: ${error.message}`);
				setLoadingCurrentMonthAttention(false);
			}
		);
		return unsubscribe;
	}, [authReady, today, viewingCurrentMonth]);

	const dailyBriefBookings = viewingCurrentMonth ? monthBookings : currentMonthAttentionBookings;
	const loadingDailyBrief = viewingCurrentMonth ? loadingMonth : loadingCurrentMonthAttention;

	// ── Last 6 months bookings for trend chart ───────────────────────────────
	const [historyBookings, setHistoryBookings] = useState([]);

	useEffect(() => {
		if (!authReady) return;
		let cancelled = false;
		const months = [];
		for (let i = 5; i >= 0; i--) {
			const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
			months.push({ year: d.getFullYear(), month: d.getMonth(), label: MONTH_SHORT[d.getMonth()] });
		}

		Promise.all(months.map((month) => {
			const ym = toYearMonth(month.year, month.month);
			const q = query(collection(db, "bookings"), where("yearMonth", "==", ym));
			return getCountFromServer(q);
		})).then((snapshots) => {
			if (cancelled) return;
			setHistoryBookings(months.map((month, index) => ({
				label: month.label,
				count: snapshots[index].data().count,
			})));
		}).catch((error) => {
			if (!cancelled) {
				console.error("Could not load booking trend counts:", error);
				setQueryError(`Could not load booking trend counts: ${error.message}`);
			}
		});

		return () => { cancelled = true; };
	}, [authReady, today]);

	// Derived
	const byDate = useMemo(() => {
		const map = {};
		monthBookings.forEach(b => { if (!map[b.date]) map[b.date] = []; map[b.date].push(b); });
		return map;
	}, [monthBookings]);

	const totalThisMonth = monthBookings.length;
	const todayCount = (byDate[toISODate(today)] || []).length;

	// Month-over-month % change for "This month" stat card, driven off the
	// same 6-month history series used by the trend chart.
	const monthTrend = useMemo(() => {
		if (historyBookings.length < 2) return null;
		const prevCount = historyBookings[historyBookings.length - 2]?.count;
		if (!prevCount) return null;
		return Math.round(((totalThisMonth - prevCount) / prevCount) * 100);
	}, [historyBookings, totalThisMonth]);

	const serviceBreakdown = useMemo(() => {
		const services = new Map();
		monthBookings.forEach((booking) => {
			const rawTitle = String(booking.serviceTitle || booking.service || "Other").trim();
			const serviceTitle = rawTitle.split(/\s+[—–-]\s+/)[0].trim() || "Other";
			const serviceId = String(booking.serviceId || serviceTitle.toLowerCase());
			const existing = services.get(serviceId);
			if (existing) {
				existing.count += 1;
				if (serviceTitle.length < existing.title.length) existing.title = serviceTitle;
			} else {
				services.set(serviceId, { title: serviceTitle, count: 1 });
			}
		});
		return [...services.values()]
			.map(({ title, count }) => [title, count])
			.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
	}, [monthBookings]);

	const openMarketingStrategy = () => {
		setMarketingSnapshot(getMarketingSnapshot(
			monthBookings,
			historyBookings,
			`${MONTH_NAMES[month]} ${year}`
		));
	};
	const closeMarketingStrategy = useCallback(() => setMarketingSnapshot(null), []);

	// Calendar grid
	const calendarCells = useMemo(() => {
		const first = new Date(year, month, 1);
		const offset = first.getDay();
		const days = new Date(year, month + 1, 0).getDate();
		const cells = [];
		for (let i = 0; i < offset; i++) cells.push(null);
		for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
		return cells;
	}, [year, month]);

	const isAtOrBeforeCurrentMonth =
		year < today.getFullYear() ||
		(year === today.getFullYear() && month <= today.getMonth());

	const selectedBookings = byDate[selectedDate] || [];

	const handleSignOut = async () => { await signOut(auth); navigate("/admin", { replace: true }); };

	if (!authReady) return <div className="adm-loading">Loading…</div>;
	if (accessStatus === "denied" || accessStatus === "error") {
		return (
			<div className="adm-loading">
				<div role="alert">
					{accessStatus === "denied"
						? "This account does not have active dashboard access. Ask an administrator to add you."
						: queryError || "Could not verify dashboard access."}
				</div>
				<button type="button" className="adm-primary-btn" onClick={handleSignOut} style={{ marginTop: 16 }}>
					Sign out
				</button>
			</div>
		);
	}

	const isAdmin = !isTeamMember;

	const allTabs = [
		{ id: "overview", label: "Overview", icon: LayoutDashboard },
		{ id: "commercial", label: "Commercial", icon: ShoppingBag },
		{ id: "bookings", label: "Bookings", icon: CalendarDays },
		{ id: "unpaid", label: "Unpaid Customers", icon: AlertCircle },
		{ id: "services", label: "Booking Services", icon: Settings2 },
		{ id: "insights", label: "AI Based Insights", icon: TrendingUp },
		{ id: "books", label: "Books", icon: BookOpen },
		{ id: "events", label: "Events", icon: Ticket },
		{ id: "keynotes", label: "Keynotes", icon: Mic2 },
		{ id: "media", label: "Media", icon: Newspaper },
		{ id: "scanner", label: "Ticket Scanner", icon: ScanLine },
		{ id: "blogs", label: "Blog", icon: FileEdit },
		{ id: "users", label: "User Management", icon: Users },
	];
	const tabs = allTabs.filter((tab) => !isTeamMember || !TEAM_RESTRICTED_TAB_IDS.has(tab.id));

	const activeLabel = tabs.find(t => t.id === activeTab)?.label || "";

	return (
		<div className="adm-page adm-shell" data-theme={adminTheme}>


			<Sidebar
				tabs={tabs}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				collapsed={sidebarCollapsed}
				setCollapsed={setSidebarCollapsed}
			/>

			<div className="adm-main-content">
				<Topbar
					email={userEmail}
					activeLabel={activeLabel}
					onSignOut={handleSignOut}
					theme={adminTheme}
					onToggleTheme={() => setAdminTheme((theme) => theme === "dark" ? "light" : "dark")}
					isAdmin={isAdmin}
					onOpenMarketingStrategy={openMarketingStrategy}
				/>

				<main
					className="adm-main-content__body"
					id="admin-main-content"
					tabIndex="-1"
				>
					<DailyBusinessBrief
						bookings={dailyBriefBookings}
						loading={loadingDailyBrief}
						onOpenBookings={() => {
							setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
							setSelectedDate(toISODate(today));
							setActiveTab("bookings");
						}}
					/>

					{/* ── Booking notification toast (fires on new bookings) ── */}
					<BookingToast toast={toast} onDismiss={dismissToast} />

					{/* ── Permission banner ── */}
					{isAdmin && permission === "default" && (
						<div className="adm-notif-banner">
							<span>Enable notifications to get alerts for new bookings and paid purchases.</span>
							<button className="adm-primary-btn" onClick={requestPermission}>
								Enable
							</button>
						</div>
					)}
					{isAdmin && permission === "denied" && (
						<div className="adm-notif-banner adm-notif-banner--muted">
							Notifications are blocked in your browser settings — enable them for this site to get booking and purchase alerts.
						</div>

					)}
					{isAdmin && pushSupported && !subscribed && (
						<div className="adm-notif-banner">
							<span>Enable push notifications to get alerted on new bookings — even with the app closed.</span>
							<button className="adm-primary-btn" onClick={subscribeToPush}>
								Enable
							</button>
						</div>
					)}
					{isAdmin && !pushSupported && (
						<div className="adm-notif-banner adm-notif-banner--muted">
							Push isn't supported in this browser — on iPhone, add this site to your Home Screen first (Share → Add to Home Screen), then enable notifications from there.
						</div>
					)}

					{queryError && (
						<div className="adm-query-error">
							<strong>Error:</strong> {queryError}
						</div>
					)}

					{/* ══════════════════════════════════════════════════════════
	            TAB: OVERVIEW
	        ══════════════════════════════════════════════════════════ */}
					{activeTab === "overview" && (
						<div className="adm-overview">
							{/* Stat row */}
							<div className="adm-stats-row">
								<StatCard
									index={0}
									icon={CalendarClock}
									label="This month"
									value={loadingMonth ? "—" : totalThisMonth}
									sub={`${MONTH_NAMES[today.getMonth()]} ${today.getFullYear()}`}
									trend={loadingMonth ? null : monthTrend}
									accent
								/>
								<StatCard
									index={1}
									icon={CalendarDays}
									label="Today"
									value={loadingMonth ? "—" : todayCount}
									sub={new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "short" })}
									tint="purple"
								/>
								<StatCard
									index={2}
									icon={Award}
									label="Top service"
									value={serviceBreakdown[0]?.[0] ?? "—"}
									sub={serviceBreakdown[0] ? `${serviceBreakdown[0][1]} booking${serviceBreakdown[0][1] !== 1 ? "s" : ""}` : "no data yet"}
									tint="amber"
								/>
							</div>

							{/* Charts row */}
							<div className="adm-charts-row">
								<div className="adm-panel adm-panel--chart" style={{ animationDelay: "120ms" }}>
									<div className="adm-panel__heading">
										<div className="adm-panel__heading-left">
											Bookings — last 6 months
										</div>
									</div>
									<div className="adm-chart-body">
										<AreaTrendChart data={historyBookings} />
									</div>
								</div>
								<div className="adm-panel adm-panel--chart" style={{ animationDelay: "190ms" }}>
									<div className="adm-panel__heading">
										<div className="adm-panel__heading-left">
											Bookings by service
											<span className="adm-panel__heading-sub">{MONTH_NAMES[month]} · {totalThisMonth} bookings</span>
										</div>
									</div>
									<div className="adm-chart-body">
										<ServiceChart breakdown={serviceBreakdown} />
									</div>
								</div>
							</div>

							{/* Different-angle stats: fulfilment health + time-of-day pattern */}
							<div className="adm-charts-row">
								<div className="adm-panel adm-panel--chart" style={{ animationDelay: "230ms" }}>
									<div className="adm-panel__heading">
										<div className="adm-panel__heading-left">
											Booking status
											<span className="adm-panel__heading-sub">{MONTH_NAMES[month]}</span>
										</div>
									</div>
									<div className="adm-chart-body">
										<BookingStatusRings bookings={monthBookings} />
									</div>
								</div>
								<div className="adm-panel adm-panel--chart" style={{ animationDelay: "300ms" }}>
									<div className="adm-panel__heading">
										<div className="adm-panel__heading-left">
											Peak booking hours
											<span className="adm-panel__heading-sub">{MONTH_NAMES[month]}</span>
										</div>
									</div>
									<div className="adm-chart-body">
										<PeakHoursChart bookings={monthBookings} />
									</div>
								</div>
							</div>

							{/* Recent bookings */}
							<div className="adm-panel" style={{ animationDelay: "260ms" }}>
								<div className="adm-panel__heading">
									<div className="adm-panel__heading-left">Today's bookings</div>
									{todayCount > 0 && <span className="adm-badge adm-badge--blue">{todayCount}</span>}
								</div>
								{loadingMonth ? (
									<div className="adm-bookings-empty">Loading…</div>
								) : (byDate[toISODate(today)] || []).length === 0 ? (
									<div className="adm-bookings-empty">
										<div className="adm-bookings-empty__icon">📭</div>
										<div>No bookings today</div>
									</div>
								) : (
									<div className="adm-bookings-list">
										{(byDate[toISODate(today)] || []).map(b => (
											<BookingRow
												key={b.id}
												booking={b}
												onDelete={handleDeleteBooking}
												onQuickStatus={handleQuickStatusChange}
												hideFinancialDetails={isTeamMember}
													canViewMeetingLink={!isTeamMember}
											/>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{isAdmin && activeTab === "commercial" && <CommercialPanel />}
					{isAdmin && activeTab === "unpaid" && <UnpaidCustomersTab adminEmail={userEmail} />}

					{activeTab === "services" && (
						<div className="adm-panel">
							<ServiceManagementPanel standalone />
						</div>
					)}

					{/* ══════════════════════════════════════════════════════════
	            TAB: BOOKINGS CALENDAR
	        ══════════════════════════════════════════════════════════ */}
					{activeTab === "bookings" && (
						<>
							<section className="adm-booking-search">
								<form className="adm-booking-search__form" role="search" onSubmit={searchAllBookings}>
									<label className="adm-booking-search__input">
										<Search size={17} aria-hidden="true" />
										<input
											type="search"
											value={bookingSearchQuery}
											onChange={(event) => setBookingSearchQuery(event.target.value)}
											placeholder="Search all bookings by name, email, service, date, or reference"
											aria-label="Search all bookings"
										/>
									</label>
									<button className="adm-booking-search__button" type="submit" disabled={bookingSearchLoading || !bookingSearchQuery.trim()}>
										{bookingSearchLoading ? "Searching…" : "Search"}
									</button>
									{bookingSearchResults !== null && (
										<button className="adm-booking-search__clear" type="button" onClick={clearBookingSearch}>
											Back to calendar
										</button>
									)}
								</form>
								{bookingSearchError && <div className="adm-query-error" role="alert">{bookingSearchError}</div>}
								{bookingSearchLoading && <div className="adm-bookings-empty">Searching all bookings…</div>}
								{bookingSearchResults !== null && !bookingSearchLoading && (
									<>
										<div className="adm-booking-search__summary">
											<strong>{bookingSearchResults.length} result{bookingSearchResults.length === 1 ? "" : "s"}</strong>
											<span>for “{searchedBookingTerm}” · review each record before deleting it</span>
										</div>
										{bookingSearchResults.length === 0 ? (
											<div className="adm-bookings-empty">No bookings matched that search.</div>
										) : (
											<div className="adm-bookings-list adm-booking-search__results">
												{bookingSearchResults.map((booking) => (
													<BookingRow
														key={booking.id}
														booking={booking}
														onDelete={handleDeleteBooking}
														onQuickStatus={handleQuickStatusChange}
														showDate
														hideFinancialDetails={isTeamMember}
														canViewMeetingLink={!isTeamMember}
													/>
												))}
											</div>
										)}
									</>
								)}
							</section>
							{bookingSearchResults === null && !bookingSearchLoading && (
						<div className="adm-main-grid">
							{/* Calendar */}
							<div className="adm-panel">
								<div className="adm-cal-nav-row">
									<button
										className="adm-cal-nav"
										onClick={() => setViewDate(new Date(year, month - 1, 1))}
										disabled={isAtOrBeforeCurrentMonth}
										aria-label="Previous month"
									>←</button>
									<span className="adm-cal-month">
										{MONTH_NAMES[month]} {year}
										{loadingMonth && <span className="adm-cal-loading"> ·</span>}
									</span>
									<button
										className="adm-cal-nav"
										onClick={() => setViewDate(new Date(year, month + 1, 1))}
										aria-label="Next month"
									>→</button>
								</div>

								<div className="adm-cal-day-labels">
									{DAY_LABELS.map(l => <div key={l} className="adm-cal-day-label">{l}</div>)}
								</div>

								<div className="adm-cal-grid">
									{calendarCells.map((date, i) => {
										if (!date) return <div key={`e-${i}`} />;
										const iso = toISODate(date);
										const count = (byDate[iso] || []).length;
										const isSelected = selectedDate === iso;
										const isToday = iso === toISODate(today);
										return (
											<button
												key={iso}
												type="button"
												className={[
													"adm-cal-cell",
													isSelected ? "adm-cal-cell--selected" : "",
													isToday ? "adm-cal-cell--today" : "",
													count > 0 ? "adm-cal-cell--has" : "",
												].join(" ").trim()}
												onClick={() => setSelectedDate(iso)}
											>
												<span className="adm-cal-cell__num">{date.getDate()}</span>
												{count > 0 && <span className="adm-cal-cell__badge">{count}</span>}
											</button>
										);
									})}
								</div>

								{serviceBreakdown.length > 0 && (
									<div className="adm-service-legend">
										{serviceBreakdown.map(([title, count], i) => (
											<div key={title} className="adm-legend-row">
												<span className="adm-legend-dot" style={{ background: SERVICE_CHART_COLORS[i % SERVICE_CHART_COLORS.length] }} />
												<span className="adm-legend-label" title={title}>{title}</span>
												<span className="adm-legend-count">{count}</span>
											</div>
										))}
									</div>
								)}

								{/* ── Sync to phone calendar ── */}
								<CalendarSyncTrigger />

								<WeekendAvailabilityPanel theme={adminTheme} />
							</div>

							{/* Booking list */}
							<div className="adm-panel adm-panel--bookings">
								<div className="adm-panel__header">
									<div className="adm-panel__title">{formatDisplayDate(selectedDate)}</div>
									<div className="adm-panel__sub">
										{selectedBookings.length === 0
											? "No bookings"
											: `${selectedBookings.length} booking${selectedBookings.length !== 1 ? "s" : ""}`}
									</div>
								</div>
								{loadingMonth ? (
									<div className="adm-bookings-empty"><div className="adm-bookings-empty__icon">⏳</div>Loading…</div>
								) : selectedBookings.length === 0 ? (
									<div className="adm-bookings-empty"><div className="adm-bookings-empty__icon">📭</div>No bookings for this day</div>
								) : (
									<div className="adm-bookings-list">
										{selectedBookings.map(b => (
											<BookingRow
												key={b.id}
												booking={b}
												onDelete={handleDeleteBooking}
												onQuickStatus={handleQuickStatusChange}
												hideFinancialDetails={isTeamMember}
												canViewMeetingLink={!isTeamMember}
											/>
										))}
									</div>
								)}
							</div>
						</div>
							)}
						</>
					)}

					{/* ══════════════════════════════════════════════════════════
	            TAB: BOOKS
	        ══════════════════════════════════════════════════════════ */}
					{activeTab === "books" && <BooksTab db={db} />}

					{/* ══════════════════════════════════════════════════════════
	            TAB: KEYNOTES
	        ══════════════════════════════════════════════════════════ */}
					{activeTab === "keynotes" && <KeynotesTab db={db} />}

					{/* ══════════════════════════════════════════════════════════
	            TAB: MEDIA
	        ══════════════════════════════════════════════════════════ */}
					{activeTab === "media" && <MediaTab db={db} />}

					{activeTab === "insights" && <InsightsDashboard db={db} />}

					{activeTab === "events" && <EventsTab db={db} />}

					{activeTab === "scanner" && <TicketScanner />}

					{activeTab === "blogs" && <BlogsTab db={db} />}

					{activeTab === "users" && (
						<UserManagementTab
							currentAdminEmail={userEmail}
							currentAdminUid={auth.currentUser?.uid}
							canManageUsers={isAdmin}
						/>
					)}
				</main>
			</div>
			{marketingSnapshot && (
				<MarketingStrategyModal
					snapshot={marketingSnapshot}
					onClose={closeMarketingStrategy}
					theme={adminTheme}
				/>
			)}
		</div>
	);
};

export default AdminDashboard;
